import React, { useState, useEffect, useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Chip,
  Skeleton,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  AppBar,
  Toolbar,
  Dialog,
  DialogContent,
  Button,
  CircularProgress,
  TextField,
  Alert,
  SvgIcon
} from '@mui/material';
import {
  Article as ArticleIcon,
  ThumbUp,
  Visibility,
  Groups as GroupsIcon,
  Groups as AudienceIcon,
  BarChart as ChartIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  CalendarMonth as CalendarIcon,
  Dashboard as DashboardIcon,
  SentimentSatisfied,
  TrendingUp,
  NightlightRound as DarkModeIcon,
  WbSunny as LightModeIcon
} from '@mui/icons-material';
import { ReactComponent as MensureLogo } from '../../assets/logo.svg';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell, PieChart, Pie, Sector } from 'recharts';
import api from '../../services/api';
import { useTheme } from '../../design-system/providers/ThemeProvider';
import {
  operationalComponentSurfaces,
  operationalElevation,
  operationalFeedback,
  operationalFontSizes,
  operationalMotion,
  operationalRadii,
  operationalSizing,
  operationalSurfaces,
  operationalStatusColors,
  operationalText,
  operationalTypography
} from '../../design-system/tokens';
import DateRangePicker from '../../components/molecules/DateRangePicker/DateRangePicker';
import PublicArticleModal from '../../components/organisms/PublicArticleModal/PublicArticleModal';
import ArticleCard from '../../components/organisms/ArticleCard';
import WordCloudCard from '../../components/organisms/WordCloudCard/WordCloudCard';
import { ViewSettingsProvider } from '../../context/ViewSettingsContext';

const PERIOD_OPTIONS = [
  { label: '7 dias', value: 7 },
  { label: '15 dias', value: 15 },
  { label: '30 dias', value: 30 },
];

const PUBLIC_ARTICLES_PAGE_SIZE = 60;
const DEFAULT_BRAND_COLOR = '#FF5722';

const isAbsoluteUrl = (value) => /^https?:\/\//i.test(value || '');

const normalizeHexColor = (color, fallback = DEFAULT_BRAND_COLOR) => {
  if (!color || typeof color !== 'string') return fallback;
  const trimmed = color.trim();
  const shortHex = /^#([0-9a-f]{3})$/i;
  const longHex = /^#([0-9a-f]{6})$/i;
  if (longHex.test(trimmed)) return trimmed;
  if (shortHex.test(trimmed)) {
    const [, value] = trimmed.match(shortHex);
    return `#${value.split('').map((ch) => ch + ch).join('')}`;
  }
  return fallback;
};

const hexToRgb = (hex) => {
  const normalized = normalizeHexColor(hex);
  const value = normalized.slice(1);
  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16)
  };
};

const mixHexColor = (color, mixWith = '#ffffff', amount = 0.2) => {
  const a = hexToRgb(color);
  const b = hexToRgb(mixWith);
  const mix = (from, to) => Math.round(from + (to - from) * amount);
  return `#${[mix(a.r, b.r), mix(a.g, b.g), mix(a.b, b.b)]
    .map((value) => value.toString(16).padStart(2, '0'))
    .join('')}`;
};

const getRelativeLuminance = (color) => {
  const { r, g, b } = hexToRgb(color);
  const toLinear = (channel) => {
    const value = channel / 255;
    return value <= 0.03928
      ? value / 12.92
      : ((value + 0.055) / 1.055) ** 2.4;
  };

  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
};

const getContrastText = (color) => (
  getRelativeLuminance(color) > 0.56 ? '#111827' : '#ffffff'
);

const resolveBrandLogo = (logo) => {
  if (!logo) return null;
  const normalized = String(logo).trim();
  if (!normalized) return null;
  if (isAbsoluteUrl(normalized)) return normalized;
  return normalized.startsWith('/') ? normalized : `/${normalized}`;
};

const TierIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M12 3L9.5 8h5z M8.5 10L6 15h12l-2.5-5z M5 17L2.5 22h19L19 17z" />
  </SvgIcon>
);

// ── SmartScore helpers (espelho do dashboard interno) ──────────────────────────
const clampSmartScore = (value) => Math.max(-10, Math.min(10, Number(value || 0)));
const getSmartScorePercent = (value) => ((clampSmartScore(value) + 10) / 20) * 100;
const formatSignedScore = (value) => {
  const n = Number(value || 0);
  return `${n > 0 ? '+' : ''}${n.toFixed(1)}`;
};
const getSmartScoreColor = (value) => {
  const score = Number(value || 0);
  if (score >= 6) return '#2e7d32';
  if (score >= 2) return '#7cb342';
  if (score > -2) return '#f59e0b';
  if (score > -6) return '#ef6c00';
  return '#d32f2f';
};
const SMART_SCORE_STATUS_COLORS = {
  Excelente: '#2e7d32', 'Muito Bom': '#4caf50', Bom: '#7cb342',
  Regular: '#c0ca33', Alerta: '#f59e0b', Irregular: '#ef6c00',
  Ruim: '#f4511e', 'Muito Ruim': '#d32f2f', Pessimo: '#b71c1c'
};
const DEFAULT_SMART_SCORE_LEGEND = [
  { label: 'Pessimo', min: -10.0, max: -9.1, count: 0 },
  { label: 'Muito Ruim', min: -9.0, max: -6.1, count: 0 },
  { label: 'Ruim', min: -6.0, max: -4.1, count: 0 },
  { label: 'Irregular', min: -4.0, max: -2.1, count: 0 },
  { label: 'Alerta', min: -2.0, max: 2.0, count: 0 },
  { label: 'Regular', min: 2.1, max: 4.0, count: 0 },
  { label: 'Bom', min: 4.1, max: 6.0, count: 0 },
  { label: 'Muito Bom', min: 6.1, max: 9.0, count: 0 },
  { label: 'Excelente', min: 9.1, max: 10.0, count: 0 }
];
const formatSmartScoreRange = ({ min, max }) =>
  `${min > 0 ? '+' : ''}${Number(min).toFixed(1)} a ${max > 0 ? '+' : ''}${Number(max).toFixed(1)}`;
const sortSmartScoreLegend = (legend = []) =>
  [...legend].sort((a, b) => Number(a.min || 0) - Number(b.min || 0));
const formatPercent = (value) =>
  `${Number(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 1 })}%`;
const getKpiElementColor = (componentKey, element = {}, fallbackIndex = 0) => {
  const KPI_COLORS = ['#4caf50','#7cb342','#c0ca33','#f59e0b','#ef6c00','#f4511e','#d32f2f','#42a5f5'];
  const label = String(element.label || '').toLowerCase();
  if (componentKey === 'sentiment') {
    if (label.includes('positivo')) return '#4caf50';
    if (label.includes('neutro')) return '#f59e0b';
    if (label.includes('negativo')) return '#d32f2f';
  }
  if (componentKey === 'exposure') {
    if (label.includes('dedicada')) return '#4caf50';
    if (label.includes('compartilhada')) return '#7cb342';
    if (label.includes('mencao') || label.includes('menção')) return '#f59e0b';
    if (label.includes('nenhuma')) return '#d32f2f';
  }
  if (['highlight','spokesperson','keyMessage'].includes(componentKey)) {
    if (label.includes('sim') || label.includes('identificado') || label.includes('com mensagem')) return '#4caf50';
    if (label.includes('nao') || label.includes('não') || label.includes('nenhuma')) return '#d32f2f';
  }
  if (componentKey === 'reach') {
    if (label.includes('nacional')) return '#4caf50';
    if (label.includes('regional')) return '#7cb342';
    if (label.includes('local')) return '#f59e0b';
    if (label.includes('sem')) return '#d32f2f';
  }
  return KPI_COLORS[fallbackIndex % KPI_COLORS.length];
};
const getIndicatorScoreColor = (value) => {
  const score = Number(value || 0);
  if (score >= 9.1) return '#2e7d32';
  if (score >= 6.1) return '#4caf50';
  if (score >= 4.1) return '#7cb342';
  if (score >= 2.1) return '#f59e0b';
  return '#d32f2f';
};
const getStatusInsightItems = (statusItem = {}) => {
  const components = Array.isArray(statusItem.components) ? statusItem.components : [];
  const calculated = components.filter(c => Number(c.count || 0) > 0);
  const weak = calculated.filter(c => Number(c.averageScore || 0) < 4.1).slice(0, 3);
  const strong = [...calculated].sort((a, b) => Number(b.averageScore || 0) - Number(a.averageScore || 0))
    .filter(c => Number(c.averageScore || 0) >= 6.1).slice(0, 3);
  return { weak, strong };
};

const PublicDashboardPage = () => {
  const routeParams = useParams();
  const wildcardToken = routeParams['*']?.split('/').filter(Boolean).pop();
  const token = routeParams.token || wildcardToken;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const stickyHeaderRef = useRef(null);
  const [stickyHeaderHeight, setStickyHeaderHeight] = useState(0);
  const publicArticlesRef = useRef(null);
  const [publicArticlesWidth, setPublicArticlesWidth] = useState(0);
  const [articlesLimit, setArticlesLimit] = useState(PUBLIC_ARTICLES_PAGE_SIZE);
  const [loadingMoreArticles, setLoadingMoreArticles] = useState(false);
  const pinStorageKey = `publicDashboardPin:${token}`;
  const [pinRequired, setPinRequired] = useState(false);
  const [dashboardPin, setDashboardPin] = useState(() => sessionStorage.getItem(`publicDashboardPin:${token}`) || '');
  const [pinError, setPinError] = useState('');
  const [pinSubmitting, setPinSubmitting] = useState(false);

  const publicColumns = useMemo(() => {
    if (publicArticlesWidth >= 1800) return 7;
    if (publicArticlesWidth >= 1500) return 6;
    if (publicArticlesWidth >= 1180) return 5;
    if (publicArticlesWidth >= 900) return 4;
    if (publicArticlesWidth >= 560) return 3;
    if (publicArticlesWidth >= 360) return 2;
    return 1;
  }, [publicArticlesWidth]);

  const alignArticlesLimitToRows = useCallback((limit, total = null) => {
    const columns = Math.max(publicColumns, 1);
    const alignedLimit = Math.ceil(limit / columns) * columns;
    return total ? Math.min(alignedLimit, total) : alignedLimit;
  }, [publicColumns]);

  // Filtros de data
  const [days, setDays] = useState(7);
  const [customDateRange, setCustomDateRange] = useState(null); // { startDate, endDate, dateType }
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Filtro de tag
  const [selectedTagIds, setSelectedTagIds] = useState([]);

  // Gráfico global - métrica e tipo
  const [chartMetric, setChartMetric] = useState('articles');
  const [chartType, setChartType] = useState('bar');

  // Gráfico individual - Evolução Diária
  const [evolutionMetric, setEvolutionMetric] = useState(null);
  const [evolutionType, setEvolutionType] = useState(null);

  // Gráfico individual - Plataformas
  const [platformMetric, setPlatformMetric] = useState(null);
  const [platformType, setPlatformType] = useState(null);

  // Gráfico individual - Marca vs Concorrentes
  const [competitorMetric, setCompetitorMetric] = useState(null);
  const [competitorType, setCompetitorType] = useState(null);

  // Gráfico individual - Fontes
  const [sourceMetric, setSourceMetric] = useState(null);
  const [sourceType, setSourceType] = useState(null);
  const [sourceLimit, setSourceLimit] = useState(10);

  // Gráfico de Evolução - tipo de agregação (dia/mês)
  const [evolutionGroupBy, setEvolutionGroupBy] = useState('day');

  // Gráfico por dia da semana - média ou total
  const [weekdayView, setWeekdayView] = useState('avg');

  // Gráfico de Evolução - escolha marca ou marca + concorrentes
  const [evolutionMode, setEvolutionMode] = useState('brand'); // 'brand' | 'all'
  const [competitorEvolutionGroupBy, setCompetitorEvolutionGroupBy] = useState('day');
  const [competitorEvolutionMetric, setCompetitorEvolutionMetric] = useState('articles');

  // Coleções para comparar
  const [selectedCollections, setSelectedCollections] = useState([]);
  const [collectionMetric, setCollectionMetric] = useState('articles');
  const [collectionGroupBy, setCollectionGroupBy] = useState('day');

  // Toggle entre análise por Coleções ou por Keywords do Briefing
  const [analysisMode, setAnalysisMode] = useState('collections'); // 'collections' | 'keywords'
  const [publicWordLimit, setPublicWordLimit] = useState(200);
  const [publicCloudType, setPublicCloudType] = useState('words');
  const [publicWordContext, setPublicWordContext] = useState('all');

  // Card expandido
  const [selectedArticle, setSelectedArticle] = useState(null);

  // Busca de artigos
  const [searchQuery] = useState('');

  // Mostrar gráficos
  const [showCharts, setShowCharts] = useState(false);
  const [chartsModalOpen, setChartsModalOpen] = useState(false);
  const [publicTheme, setPublicTheme] = useState(() => {
    const saved = localStorage.getItem('publicDashboardTheme');
    return saved === 'light' || saved === 'escuro' ? saved : 'escuro';
  });

  // Scroll state
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useLayoutEffect(() => {
    const element = stickyHeaderRef.current;
    if (!element) return undefined;

    const updateHeight = () => setStickyHeaderHeight(element.getBoundingClientRect().height);
    updateHeight();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateHeight);
      return () => window.removeEventListener('resize', updateHeight);
    }

    const observer = new ResizeObserver(updateHeight);
    observer.observe(element);
    return () => observer.disconnect();
  }, [data?.availableTags?.length, publicTheme, data]);

  const { setTheme, setFontFamily } = useTheme();

  useEffect(() => {
    setTheme(publicTheme);
    setFontFamily('Montserrat, sans-serif');
    localStorage.setItem('publicDashboardTheme', publicTheme);
  }, [publicTheme, setTheme, setFontFamily]);

  const handlePublicThemeChange = (_, nextTheme) => {
    if (!nextTheme) return;
    setPublicTheme(nextTheme);
  };

  const loadDashboard = useCallback(async (limit = PUBLIC_ARTICLES_PAGE_SIZE, options = {}) => {
    const isLoadingMore = !!options.loadingMore;
    const pin = options.pin ?? sessionStorage.getItem(pinStorageKey);

    try {
      if (isLoadingMore) {
        setLoadingMoreArticles(true);
      } else {
        setLoading(true);
      }
      let url = `/public/dashboard/${token}`;
      const params = new URLSearchParams();
      params.set('articlesLimit', limit);

      if (customDateRange?.startDate && customDateRange?.endDate) {
        // Modo personalizado — enviar datas exatas + dateType
        const fmt = (d) => format(d, 'yyyy-MM-dd');
        params.set('startDate', fmt(customDateRange.startDate));
        params.set('endDate', fmt(customDateRange.endDate));
        params.set('dateType', customDateRange.dateType || 'published');
      } else {
        // Modo rápido — últimos N dias
        params.set('days', days);
      }

      if (selectedTagIds.length > 0) {
        selectedTagIds.forEach(tagId => params.append('tagIds', tagId));
      }

      if (pin) {
        params.set('pin', pin);
      }

      const response = await api.get(`${url}?${params.toString()}`);
      setData(response.data.data);
      setError(null);
      setPinRequired(false);
      setPinError('');
      if (pin) {
        sessionStorage.setItem(pinStorageKey, pin);
      }
    } catch (err) {
      console.error('Erro ao carregar dashboard:', err);
      if (err.response?.status === 401 && err.response?.data?.code === 'PIN_REQUIRED') {
        setPinRequired(true);
        setPinError(err.response?.data?.message || 'Informe o PIN para acessar este dashboard');
        if (pin) {
          sessionStorage.removeItem(pinStorageKey);
        }
        return;
      }
      setError(err.response?.data?.message || 'Link inválido ou expirado');
    } finally {
      if (isLoadingMore) {
        setLoadingMoreArticles(false);
      } else {
        setLoading(false);
      }
    }
  }, [token, days, customDateRange, selectedTagIds, pinStorageKey]);

  const handlePinSubmit = async (event) => {
    event.preventDefault();
    const normalizedPin = dashboardPin.trim();

    if (!normalizedPin) {
      setPinError('Informe o PIN para continuar');
      return;
    }

    setPinSubmitting(true);
    await loadDashboard(articlesLimit || PUBLIC_ARTICLES_PAGE_SIZE, { pin: normalizedPin });
    setPinSubmitting(false);
  };

  useEffect(() => {
    const initialLimit = alignArticlesLimitToRows(PUBLIC_ARTICLES_PAGE_SIZE);
    setArticlesLimit(initialLimit);
    loadDashboard(initialLimit);
  }, [alignArticlesLimitToRows, loadDashboard]);

  const handleLoadMoreArticles = async () => {
    if (loadingMoreArticles) return;
    const nextLimit = alignArticlesLimitToRows(
      articlesLimit + PUBLIC_ARTICLES_PAGE_SIZE,
      data?.stats?.totalArticles
    );
    setArticlesLimit(nextLimit);
    await loadDashboard(nextLimit, { loadingMore: true });
  };

  useEffect(() => {
    const loadedArticles = data?.recentArticles?.length || 0;
    const totalArticles = data?.stats?.totalArticles || 0;

    if (
      loading ||
      loadingMoreArticles ||
      publicColumns <= 1 ||
      loadedArticles === 0 ||
      loadedArticles >= totalArticles
    ) {
      return;
    }

    const remainder = loadedArticles % publicColumns;
    if (remainder === 0) return;

    const nextAlignedLimit = alignArticlesLimitToRows(
      loadedArticles + (publicColumns - remainder),
      totalArticles
    );

    if (nextAlignedLimit <= loadedArticles || nextAlignedLimit === articlesLimit) return;

    setArticlesLimit(nextAlignedLimit);
    loadDashboard(nextAlignedLimit, { loadingMore: true });
  }, [
    alignArticlesLimitToRows,
    articlesLimit,
    data?.recentArticles?.length,
    data?.stats?.totalArticles,
    loadDashboard,
    loading,
    loadingMoreArticles,
    publicColumns
  ]);

  useLayoutEffect(() => {
    const element = publicArticlesRef.current;
    if (!element) return undefined;

    const updateWidth = () => setPublicArticlesWidth(element.getBoundingClientRect().width);
    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }

    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);
    return () => observer.disconnect();
  }, [loading, data]);

  const handlePeriodChange = (_, newDays) => {
    if (newDays !== null) {
      setCustomDateRange(null); // limpa filtro personalizado
      setDays(newDays);
    }
  };

  const handleTagToggle = (tagId) => {
    setSelectedTagIds(prev => 
      prev.includes(tagId)
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
  };

  const clearTags = () => {
    setSelectedTagIds([]);
  };

  const handleDateRangeApply = (range) => {
    setCustomDateRange(range);
    setShowDatePicker(false);
  };

  const clearCustomDate = () => {
    setCustomDateRange(null);
  };

  const [articleModalOpen, setArticleModalOpen] = useState(false);
  const [currentArticleIndex, setCurrentArticleIndex] = useState(0);

  const handleArticleClick = (article) => {
    const idx = filteredArticles.findIndex(a => a._id === article._id);
    setCurrentArticleIndex(idx >= 0 ? idx : 0);
    setSelectedArticle(article);
    setArticleModalOpen(true);
  };

  const handlePrevArticle = () => {
    if (currentArticleIndex > 0) {
      const newIdx = currentArticleIndex - 1;
      setCurrentArticleIndex(newIdx);
      setSelectedArticle(filteredArticles[newIdx]);
    }
  };

  const handleNextArticle = () => {
    if (currentArticleIndex < filteredArticles.length - 1) {
      const newIdx = currentArticleIndex + 1;
      setCurrentArticleIndex(newIdx);
      setSelectedArticle(filteredArticles[newIdx]);
    }
  };

  const handleCloseArticleModal = () => {
    setArticleModalOpen(false);
    setSelectedArticle(null);
  };

  // Label do botão de período personalizado
  const dateRangeLabel = customDateRange?.startDate && customDateRange?.endDate
    ? `${format(customDateRange.startDate, 'dd/MM/yy', { locale: ptBR })} – ${format(customDateRange.endDate, 'dd/MM/yy', { locale: ptBR })}`
    : 'Período Personalizado';

  const dateTypeLabel = customDateRange?.dateType === 'created' ? 'Cadastro' : 'Publicação';

  const publicGridItem = useMemo(() => {
    const gridUnit = 12 / Math.max(publicColumns, 1);
    return {
      xs: 12,
      sm: publicColumns >= 3 ? 4 : 6,
      md: publicColumns >= 4 ? 3 : gridUnit,
      lg: gridUnit,
      xl: gridUnit
    };
  }, [publicColumns]);

  /* ── LOADING ── */
  if (loading && !pinRequired) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: operationalSurfaces.dark.shell, py: 6 }}>
        <Container maxWidth="lg">
          <Skeleton variant="text" width={260} height={56} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width={180} height={24} sx={{ mb: 5 }} />
          <Divider sx={{ mb: 5 }} />
          <Grid container spacing={3} sx={{ mb: 5 }}>
            {[1, 2, 3, 4].map(i => (
              <Grid item xs={6} md={3} key={i}>
                <Skeleton variant="rectangular" height={130} sx={{ borderRadius: 2 }} />
              </Grid>
            ))}
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
            </Grid>
            <Grid item xs={12} md={8}>
              <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
            </Grid>
          </Grid>
        </Container>
      </Box>
    );
  }

  if (pinRequired) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: operationalSurfaces.dark.shell,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 2, sm: 3 },
          py: 4
        }}
      >
        <Box
          component="form"
          onSubmit={handlePinSubmit}
          sx={{
            width: '100%',
            maxWidth: 400,
            p: { xs: 2.5, sm: 3 },
            borderRadius: `${operationalRadii.modal}px`,
            border: '1px solid',
            borderColor: operationalSurfaces.dark.border,
            bgcolor: operationalSurfaces.dark.panel,
            boxShadow: operationalElevation.modal
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: operationalText.dark.primary,
              fontWeight: 750,
              letterSpacing: 0,
              lineHeight: 1.15,
              mb: 1
            }}
          >
            Acesso protegido
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: operationalText.dark.muted,
              fontSize: operationalFontSizes.sm,
              lineHeight: 1.45,
              mb: 2.5
            }}
          >
            Este dashboard publico exige um PIN. Use o codigo enviado junto com o link.
          </Typography>
          {pinError && (
            <Alert
              severity="warning"
              sx={{
                mb: 2,
                borderRadius: `${operationalRadii.control}px`,
                border: '1px solid',
                borderLeft: `${operationalFeedback.alert.accentBorderWidth}px solid ${operationalStatusColors.warning}`,
                borderColor: operationalSurfaces.dark.border,
                bgcolor: operationalSurfaces.dark.panelRaised,
                color: operationalText.dark.primary,
                '& .MuiAlert-icon': {
                  color: operationalStatusColors.warning
                }
              }}
            >
              {pinError}
            </Alert>
          )}
          <TextField
            fullWidth
            autoFocus
            label="PIN"
            value={dashboardPin}
            onChange={(event) => setDashboardPin(event.target.value)}
            type="password"
            autoComplete="off"
            inputProps={{
              autoComplete: 'off',
              inputMode: 'text',
              spellCheck: 'false'
            }}
            size="small"
            sx={{
              mb: 2,
              '& .MuiInputLabel-root': {
                color: operationalText.dark.muted
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: operationalText.dark.primary
              },
              '& .MuiOutlinedInput-root': {
                minHeight: operationalSizing.default,
                borderRadius: `${operationalRadii.control}px`,
                color: operationalText.dark.primary,
                bgcolor: operationalSurfaces.dark.panelRaised,
                '& fieldset': {
                  borderColor: operationalSurfaces.dark.border
                },
                '&:hover fieldset': {
                  borderColor: operationalText.dark.muted
                },
                '&.Mui-focused fieldset': {
                  borderColor: operationalText.dark.primary,
                  borderWidth: 1
                }
              }
            }}
          />
          <Button
            fullWidth
            type="submit"
            variant="contained"
            disabled={pinSubmitting}
            startIcon={pinSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              minHeight: operationalSizing.default,
              fontWeight: 750,
              borderRadius: `${operationalRadii.control}px`,
              textTransform: 'none',
              bgcolor: operationalText.dark.primary,
              color: operationalSurfaces.dark.shell,
              boxShadow: operationalElevation.none,
              '&:hover': {
                bgcolor: '#ffffff',
                boxShadow: operationalElevation.none
              },
              '&.Mui-disabled': {
                bgcolor: operationalSurfaces.dark.panelRaised,
                color: operationalText.dark.quiet
              }
            }}
          >
            {pinSubmitting ? 'Validando...' : 'Entrar no dashboard'}
          </Button>
        </Box>
      </Box>
    );
  }

  /* ── ERROR ── */
  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: operationalSurfaces.dark.shell, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ textAlign: 'center', maxWidth: 400, px: 3 }}>
          <Box
            component={MensureLogo}
            aria-hidden
            sx={{
              width: { xs: 118, sm: 136 },
              height: 'auto',
              mb: 3,
              color: 'text.primary',
              opacity: 0.9
            }}
          />
          <Typography variant="h4" fontWeight={800} color="text.primary" gutterBottom>
            Dashboard Indisponível
          </Typography>
          <Typography variant="body1" color="text.secondary">{error}</Typography>
        </Box>
      </Box>
    );
  }

  const { brand, token: tokenInfo, stats, byPlatform, bySource = [], byCategory = [], byScope = [], byUF = [], bySentiment = [], byExposure = [], byTier = [], tagStats = [], wordCloud = [], keywordsCloud = [], recentArticles = [], availableTags = [], dailyEvolution = [], brandVsCompetitors = [], competitorEvolution = [], collections = [], collectionStats = [], collectionEvolution = [] } = data;

  // SmartScore do backend
  const smartScoreGlobal = stats.smartScore || {};
  const smartScoreValue = Number(smartScoreGlobal.score || 0);
  const smartScoreTargetValue = Number(smartScoreGlobal.targetScore || 6.1);
  const smartScorePercent = getSmartScorePercent(smartScoreValue);
  const smartScoreTargetPercent = getSmartScorePercent(smartScoreTargetValue);
  const smartScoreColor = getSmartScoreColor(smartScoreValue);
  const smartScoreEnabled = Boolean(smartScoreGlobal.enabled);
  const smartScoreLegend = Array.isArray(smartScoreGlobal.statusLegend) && smartScoreGlobal.statusLegend.length
    ? sortSmartScoreLegend(smartScoreGlobal.statusLegend)
    : DEFAULT_SMART_SCORE_LEGEND;
  const smartScoreComponents = Array.isArray(smartScoreGlobal.components)
    ? smartScoreGlobal.components.filter(c => c.count > 0)
    : [];
  const smartScoreStatusTotal = smartScoreLegend.reduce((sum, item) => sum + Number(item.count || 0), 0);
  const smartScoreTargetBase = Math.max(Math.abs(smartScoreTargetValue), 0.1);
  const smartScoreTargetProgressPercent = smartScoreEnabled
    ? Math.max(0, (smartScoreValue / smartScoreTargetBase) * 100)
    : 0;

  // Métricas de audiência detalhada (idêntico ao interno)
  const audienceMonthly = stats.exposure?.monthly || 0;
  const audienceDaily = stats.exposure?.daily || 0;
  const followersTotal = stats.exposure?.followers || 0;
  const socialViewsTotal = stats.exposure?.views || 0;
  const audienceByPlatformData = stats.exposure?.byPlatform || [];
  const visitsCorrespondence = audienceByPlatformData
    .filter(item => item.audienceMonthly > 0 || item.audienceDaily > 0)
    .slice(0, 4)
    .map(item => `${item.name}: ${item.audienceDaily.toLocaleString('pt-BR')}/dia`)
    .join(' | ');
  const followersCorrespondence = audienceByPlatformData
    .filter(item => item.followers > 0)
    .slice(0, 4)
    .map(item => `${item.name}: ${item.followers.toLocaleString('pt-BR')}`)
    .join(' | ');
  const socialViewsCorrespondence = audienceByPlatformData
    .filter(item => item.views > 0)
    .slice(0, 4)
    .map(item => `${item.name}: ${item.views.toLocaleString('pt-BR')}`)
    .join(' | ');

  // Valoração em USD
  const valuationTotalBrl = stats.valuation?.total || stats.totalValuation || 0;
  const valuationTotalUsd = stats.valuation?.totalUsd || 0;
  const usdRate = stats.valuation?.usdRate || 0;

  // PR Results
  const prResults = stats.prResults || {};
  const selectedTags = availableTags.filter(t => selectedTagIds.includes(t._id));
  const brandLogo = resolveBrandLogo(brand?.logo);
  const brandColor = normalizeHexColor(brand?.primaryColor || brand?.color || brand?.brandColor, '#FF5722');
  const brandBorder = mixHexColor(brandColor, '#ffffff', 0.35);
  const brandSurface = mixHexColor(brandColor, '#0b0d11', 0.78);
  const brandSoft = mixHexColor(brandColor, '#0b0d11', 0.88);
  const brandContrastText = getContrastText(brandColor);
  const brandContrastSoft = brandContrastText === '#ffffff' ? 'rgba(255,255,255,0.78)' : 'rgba(17,24,39,0.72)';
  const brandContrastMuted = brandContrastText === '#ffffff' ? 'rgba(255,255,255,0.26)' : 'rgba(17,24,39,0.18)';
  const brandContrastHover = brandContrastText === '#ffffff' ? 'rgba(255,255,255,0.16)' : 'rgba(17,24,39,0.10)';
  const selectedControlBg = brandContrastText === '#ffffff' ? '#ffffff' : '#111827';
  const selectedControlColor = brandContrastText === '#ffffff' ? 'var(--wl-primary)' : '#ffffff';
  const isPublicLight = publicTheme === 'light';
  const publicMode = isPublicLight ? 'light' : 'dark';
  const publicSurface = operationalSurfaces[publicMode];
  const publicText = operationalText[publicMode];
  const shellBg = publicSurface.shell;
  const panelBg = publicSurface.panel;
  const panelRaisedBg = publicSurface.panelRaised;
  const panelBorder = publicSurface.border;
  const panelHover = publicSurface.hover;
  const chartGridStroke = isPublicLight ? 'rgba(15, 23, 42, 0.08)' : 'rgba(255, 255, 255, 0.08)';
  const chartAxisStroke = publicText.quiet;
  const brandHeaderBg = brandColor;
  const headerTitleColor = brandContrastText;
  const headerSubtitleColor = brandContrastSoft;
  const chartTooltipSx = {
    backgroundColor: panelRaisedBg,
    border: `1px solid ${panelBorder}`,
    borderRadius: operationalRadii.control,
    color: publicText.primary,
    fontSize: 12,
    boxShadow: operationalElevation.surface
  };
  const toggleButtonGroupSx = {
    '& .MuiToggleButton-root': {
      px: 1,
      py: 0.35,
      minHeight: 32,
      border: '1px solid',
      borderColor: panelBorder,
      borderRadius: `${operationalRadii.control}px !important`,
      color: 'text.secondary',
      fontSize: operationalFontSizes.xs,
      fontWeight: 650,
      textTransform: 'none',
      transition: `background-color ${operationalMotion.quick}, border-color ${operationalMotion.quick}`,
      '&:hover': {
        bgcolor: panelHover,
      },
      '&.Mui-selected': {
        bgcolor: 'var(--wl-primary)',
        color: brandContrastText,
        borderColor: 'var(--wl-primary)'
      },
      '&.Mui-selected:hover': {
        bgcolor: 'var(--wl-primary)'
      }
    }
  };
  const sectionPanelSx = {
    p: 2,
    borderRadius: `${operationalRadii.surface}px`,
    border: '1px solid',
    borderColor: panelBorder,
    bgcolor: panelBg,
    boxShadow: operationalElevation.none
  };
  const sectionTitleSx = {
    fontSize: operationalFontSizes.lg,
    fontWeight: 700,
    color: 'text.primary',
    letterSpacing: 0,
    mb: 2
  };

  // Ordenar dados dos gráficos por métrica selecionada (decrescente)
  const getSortValue = (item, metric) => {
    switch (metric) {
      case 'audience': return item.audience || item.totalAudience || 0;
      case 'valuation': return item.valuation || item.totalValuation || 0;
      default: return item.articles || item.count || 0;
    }
  };

  const formatNumberBR = (value) => {
    return Number(value).toLocaleString('pt-BR');
  };

  const sortedPlatforms = [...byPlatform].sort((a, b) => {
    return getSortValue(b, platformMetric || chartMetric) - getSortValue(a, platformMetric || chartMetric);
  });

  const sortedCompetitors = [...brandVsCompetitors].sort((a, b) => {
    return getSortValue(b, competitorMetric || chartMetric) - getSortValue(a, competitorMetric || chartMetric);
  });

  const sortedSources = [...bySource].sort((a, b) => {
    return getSortValue(b, sourceMetric || chartMetric) - getSortValue(a, sourceMetric || chartMetric);
  }).slice(0, sourceLimit);

  // Agrupar dados de evolução por mês
  const getEvolutionData = () => {
    if (evolutionGroupBy === 'month') {
      const monthlyData = {};
      dailyEvolution.forEach(d => {
        const date = parseISO(d._id);
        const monthKey = format(date, 'MMM/yyyy', { locale: ptBR });
        if (!monthlyData[monthKey]) {
          monthlyData[monthKey] = { articles: 0, audience: 0, valuation: 0 };
        }
        monthlyData[monthKey].articles += d.articles;
        monthlyData[monthKey].audience += d.audience;
        monthlyData[monthKey].valuation += d.valuation;
      });
      return Object.entries(monthlyData).map(([month, data]) => ({
        date: month,
        articles: data.articles,
        audience: data.audience,
        valuation: data.valuation
      }));
    }
    return dailyEvolution.map(d => ({
      date: format(parseISO(d._id), 'dd/MM', { locale: ptBR }),
      articles: d.articles,
      audience: d.audience,
      valuation: d.valuation
    }));
  };

  // Agrupar dados por dia da semana
  const getWeekdayData = () => {
    const weekdays = [
      { key: 0, name: 'Domingo', shortName: 'Dom' },
      { key: 1, name: 'Segunda', shortName: 'Seg' },
      { key: 2, name: 'Terça', shortName: 'Ter' },
      { key: 3, name: 'Quarta', shortName: 'Qua' },
      { key: 4, name: 'Quinta', shortName: 'Qui' },
      { key: 5, name: 'Sexta', shortName: 'Sex' },
      { key: 6, name: 'Sábado', shortName: 'Sáb' }
    ];

    const weekdayTotals = weekdays.map(w => ({ ...w, articles: 0, audience: 0, valuation: 0, count: 0 }));

    dailyEvolution.forEach(d => {
      const date = parseISO(d._id);
      const weekdayIndex = date.getDay();
      weekdayTotals[weekdayIndex].articles += d.articles;
      weekdayTotals[weekdayIndex].audience += d.audience;
      weekdayTotals[weekdayIndex].valuation += d.valuation;
      weekdayTotals[weekdayIndex].count += 1;
    });

    return weekdayTotals.map(w => ({
      name: w.shortName,
      fullName: w.name,
      articles: w.count > 0 ? Math.round(w.articles / w.count) : 0,
      audience: w.count > 0 ? Math.round(w.audience / w.count) : 0,
      valuation: w.count > 0 ? Math.round(w.valuation / w.count) : 0,
      totalArticles: w.articles,
      totalAudience: w.audience,
      totalValuation: w.valuation
    }));
  };

  // Agrupar dados de evolução Marca vs Concorrentes por mês
  const getCompetitorEvolutionData = () => {
    if (!competitorEvolution || competitorEvolution.length === 0) return [];

    if (competitorEvolutionGroupBy === 'month') {
      const monthlyData = {};

      competitorEvolution.forEach(d => {
        const date = parseISO(d.date);
        const monthKey = format(date, 'MMM/yyyy', { locale: ptBR });

        if (!monthlyData[monthKey]) {
          monthlyData[monthKey] = { date: monthKey };
          Object.keys(d).forEach(key => {
            if (key !== 'date') {
              monthlyData[monthKey][key] = { articles: 0, audience: 0, valuation: 0, shareOfVoice: 0 };
            }
          });
        }

        Object.keys(d).forEach(key => {
          if (key !== 'date' && d[key]) {
            monthlyData[monthKey][key].articles += d[key].articles || 0;
            monthlyData[monthKey][key].audience += d[key].audience || 0;
            monthlyData[monthKey][key].valuation += d[key].valuation || 0;
          }
        });
      });

      // Calcular dados mensais baseado na métrica selecionada
      return Object.values(monthlyData).map(monthData => {
        const totalArticles = Object.values(monthData)
          .filter(v => v && typeof v === 'object' && v.articles !== undefined)
          .reduce((sum, v) => sum + v.articles, 0);

        const result = { date: monthData.date };
        Object.keys(monthData).forEach(key => {
          if (key !== 'date') {
            if (competitorEvolutionMetric === 'shareOfVoice') {
              result[key] = totalArticles > 0 ? ((monthData[key].articles / totalArticles) * 100).toFixed(1) : 0;
            } else {
              result[key] = monthData[key][competitorEvolutionMetric] || 0;
            }
          }
        });
        return result;
      }).sort((a, b) => {
        const monthOrder = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
        const [aMonth, aYear] = a.date.split('/');
        const [bMonth, bYear] = b.date.split('/');
        if (aYear !== bYear) return parseInt(aYear) - parseInt(bYear);
        return monthOrder.indexOf(aMonth) - monthOrder.indexOf(bMonth);
      });
    }

    // Daily data - flatten to use selected metric
    return competitorEvolution.map(d => {
      const result = { date: format(parseISO(d.date), 'dd/MM', { locale: ptBR }) };
      Object.keys(d).forEach(key => {
        if (key !== 'date' && d[key]) {
          if (competitorEvolutionMetric === 'shareOfVoice') {
            result[key] = d[key].shareOfVoice || 0;
          } else {
            result[key] = d[key][competitorEvolutionMetric] || 0;
          }
        }
      });
      return result;
    });
  };

  // Agrupar dados de evolução por Coleções
  const getCollectionEvolutionData = () => {
    if (!collectionEvolution || collectionEvolution.length === 0) return [];

    const collectionsToUse = selectedCollections.length > 0
      ? collectionStats.filter(c => selectedCollections.includes(c.name))
      : collectionStats.slice(0, 5);

    if (collectionGroupBy === 'month') {
      const monthlyData = {};

      collectionEvolution.forEach(d => {
        const date = parseISO(d.date);
        const monthKey = format(date, 'MMM/yyyy', { locale: ptBR });

        if (!monthlyData[monthKey]) {
          monthlyData[monthKey] = { date: monthKey };
          collectionsToUse.forEach(col => {
            monthlyData[monthKey][col.name] = { articles: 0, audience: 0, valuation: 0 };
          });
        }

        collectionsToUse.forEach(col => {
          if (d[col.name]) {
            monthlyData[monthKey][col.name].articles += d[col.name].articles || 0;
            monthlyData[monthKey][col.name].audience += d[col.name].audience || 0;
            monthlyData[monthKey][col.name].valuation += d[col.name].valuation || 0;
          }
        });
      });

      return Object.values(monthlyData).map(monthData => {
        const result = { date: monthData.date };
        collectionsToUse.forEach(col => {
          if (collectionMetric === 'articles') {
            result[col.name] = monthData[col.name].articles;
          } else if (collectionMetric === 'audience') {
            result[col.name] = monthData[col.name].audience;
          } else {
            result[col.name] = monthData[col.name].valuation;
          }
        });
        return result;
      }).sort((a, b) => {
        const monthOrder = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
        const [aMonth, aYear] = a.date.split('/');
        const [bMonth, bYear] = b.date.split('/');
        if (aYear !== bYear) return parseInt(aYear) - parseInt(bYear);
        return monthOrder.indexOf(aMonth) - monthOrder.indexOf(bMonth);
      });
    }

    return collectionEvolution.map(d => {
      const result = { date: format(parseISO(d.date), 'dd/MM', { locale: ptBR }) };
      collectionsToUse.forEach(col => {
        if (d[col.name]) {
          if (collectionMetric === 'articles') {
            result[col.name] = d[col.name].articles;
          } else if (collectionMetric === 'audience') {
            result[col.name] = d[col.name].audience;
          } else {
            result[col.name] = d[col.name].valuation;
          }
        }
      });
      return result;
    });
  };

  // Artigos filtrados pela busca
  const filteredArticles = searchQuery.trim()
    ? recentArticles.filter(a => 
        a.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.siteName?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : recentArticles;
  const totalAvailableArticles = stats.totalArticles || 0;
  const hasMoreArticles = recentArticles.length < totalAvailableArticles;

  // Formata números grandes de forma compacta e legível
  const formatBigNumber = (n) => {
    if (!n || n === 0) return '0';
    if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1).replace(/\.0$/, '')}B`;
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
    return n.toLocaleString('pt-BR');
  };

  const formatCurrency = (n) => {
    if (!n || n === 0) return 'R$ 0';
    if (n >= 1_000_000_000) return `R$ ${(n / 1_000_000_000).toFixed(1).replace(/\.0$/, '')}B`;
    if (n >= 1_000_000) return `R$ ${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
    if (n >= 1_000) return `R$ ${(n / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
    return `R$ ${n.toLocaleString('pt-BR')}`;
  };

  // Cores para gráficos de pizza
  const pieColors = ['#FF5722', '#3b82f6', '#22c55e', '#f59e0b', '#ec4899', '#8b5cf6', '#06b6d4', '#a855f7', '#64748b', '#ef4444'];
  
  // Gerar cor consistente baseada no nome da marca/concorrente
  const getCompetitorColor = (name, isBrand, index) => {
    if (isBrand) return '#FF5722';
    // Hash do nome para cor consistente
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return pieColors[Math.abs(hash) % pieColors.length];
  };

  // Componente para gráfico de pizza ativo
  const renderActiveShape = (props) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload } = props;
    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius + 8}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius - 4}
          outerRadius={innerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
      </g>
    );
  };

  const normalizeCountData = (items = []) => items
    .map((item) => ({
      name: item.name || item._id || item.text || 'Nao informado',
      value: Number(item.count ?? item.value ?? 0)
    }))
    .filter((item) => item.name && item.value > 0);

  const ensureCountData = (items = [], expected = []) => {
    const sourceItems = Array.isArray(items)
      ? items
      : Object.entries(items || {}).map(([key, count]) => ({ _id: key, count }));
    const byKey = new Map(sourceItems.map((item) => [String(item._id ?? item.name), Number(item.count || 0)]));
    return expected.map((item) => ({
      name: item.name,
      value: byKey.get(String(item.key)) || byKey.get(item.name) || 0
    })).filter((item) => item.value > 0);
  };

  const countValue = (items = [], key, label) => {
    const item = (items || []).find((entry) => String(entry._id) === String(key) || entry.name === label);
    return Number(item?.count || item?.value || 0);
  };

  const platformCountChart = normalizeCountData((byPlatform || []).map((item) => ({
    name: item._id || 'Nao informado',
    count: item.count
  })));
  const categoryChart = normalizeCountData(byCategory);
  const scopeChart = normalizeCountData(byScope);
  const ufChart = normalizeCountData(byUF);
  const sentimentChart = ensureCountData(bySentiment.length ? bySentiment : stats.charts?.bySentiment, [
    { key: 'positive', name: 'Positivo' },
    { key: 'neutral', name: 'Neutro' },
    { key: 'negative', name: 'Negativo' }
  ]);
  const exposureChart = ensureCountData(byExposure.length ? byExposure : stats.charts?.byExposure, [
    { key: 'dedicated', name: 'Dedicada' },
    { key: 'shared', name: 'Compartilhada' },
    { key: 'mention', name: 'Citação' }
  ]);
  const tierChart = ensureCountData(byTier.length ? byTier : stats.charts?.byTier, [
    { key: 1, name: 'Tier 1' },
    { key: 2, name: 'Tier 2' },
    { key: 3, name: 'Tier 3' }
  ]);
  const cloudTerms = (wordCloud.length ? wordCloud : keywordsCloud).slice(0, 80);
  const sentimentPositive = countValue(bySentiment, 'positive', 'Positivo');
  const sentimentNeutral = countValue(bySentiment, 'neutral', 'Neutro');
  const sentimentNegative = countValue(bySentiment, 'negative', 'Negativo');
  const tier1Total = countValue(byTier, 1, 'Tier 1');
  const tier2Total = countValue(byTier, 2, 'Tier 2');
  const tier3Total = countValue(byTier, 3, 'Tier 3');
  const socialLikes = stats.socialEngagement?.totalLikes || 0;
  const socialComments = stats.socialEngagement?.totalComments || 0;
  const socialShares = stats.socialEngagement?.totalShares || 0;
  const socialSaves = stats.socialEngagement?.totalSaves || 0;
  const socialTotal = stats.socialEngagement?.total || (socialLikes + socialComments + socialShares + socialSaves);
  const internalTimelineData = dailyEvolution.map((d) => ({
    date: format(parseISO(d._id), 'dd/MM', { locale: ptBR }),
    value: d.articles || 0
  }));

  const chartBoxSx = {
    ...sectionPanelSx,
    height: '100%',
    minHeight: 0,
    overflow: 'hidden'
  };

  const renderEmptyChart = (label = 'Sem dados no periodo') => (
    <Box sx={{ minHeight: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Box>
  );

  const renderCountChart = (title, items, options = {}) => {
    const dataItems = items || [];
    const type = options.type || 'pie';
    const height = options.height || 240;
    const color = options.color || '#3b82f6';

    return (
      <Box sx={chartBoxSx}>
        <Typography sx={sectionTitleSx}>{title}</Typography>
        {dataItems.length === 0 ? renderEmptyChart() : type === 'bar' ? (
          options.orientation === 'vertical' ? (
          <ResponsiveContainer width="100%" height={Math.max(height, dataItems.length * 34)}>
            <BarChart data={dataItems} layout="vertical" margin={{ left: 10, right: 24 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} horizontal={false} />
              <XAxis type="number" stroke={chartAxisStroke} fontSize={11} tickLine={false} />
              <YAxis type="category" dataKey="name" stroke={chartAxisStroke} fontSize={10} tickLine={false} width={96} />
              <Tooltip
                contentStyle={chartTooltipSx}
                formatter={(value) => [formatNumberBR(value), 'Publicacoes']}
              />
              <Bar dataKey="value" fill={color} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
          ) : (
            <ResponsiveContainer width="100%" height={height}>
              <BarChart data={dataItems} margin={{ top: 10, right: 8, left: -18, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} vertical={false} />
                <XAxis dataKey="name" stroke={chartAxisStroke} fontSize={10} tickLine={false} interval={0} />
                <YAxis stroke={chartAxisStroke} fontSize={10} tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={chartTooltipSx}
                  formatter={(value) => [formatNumberBR(value), 'Publicacoes']}
                />
                <Bar dataKey="value" fill={color} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )
        ) : (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'center', minHeight: height }}>
            <Box sx={{ width: 180, height: 180, flexShrink: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={dataItems}
                    cx="50%"
                    cy="50%"
                    innerRadius={44}
                    outerRadius={78}
                    dataKey="value"
                    nameKey="name"
                  >
                    {dataItems.map((_, index) => (
                      <Cell key={`chart-cell-${title}-${index}`} fill={pieColors[index % pieColors.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={chartTooltipSx}
                    formatter={(value, name) => [formatNumberBR(value), name]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {(() => {
                const total = dataItems.reduce((sum, item) => sum + item.value, 0);
                return dataItems.map((item, index) => (
                  <Box key={`${title}-${item.name}`} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, bgcolor: pieColors[index % pieColors.length], borderRadius: 0.5, flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.name}
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary', minWidth: 46, textAlign: 'right' }}>
                      {formatNumberBR(item.value)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled', minWidth: 46, textAlign: 'right' }}>
                      {total > 0 ? `${((item.value / total) * 100).toFixed(1)}%` : '0%'}
                    </Typography>
                  </Box>
                ));
              })()}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const renderWordCloudChart = () => {
    const maxValue = Math.max(...cloudTerms.map((item) => Number(item.value || 0)), 1);
    return (
      <Box sx={chartBoxSx}>
        <Typography sx={sectionTitleSx}>Nuvem de Palavras</Typography>
        {cloudTerms.length === 0 ? renderEmptyChart('Sem palavras no periodo') : (
          <Box sx={{ minHeight: 220, display: 'flex', flexWrap: 'wrap', alignContent: 'center', alignItems: 'center', justifyContent: 'center', gap: 1.1, px: 1 }}>
            {cloudTerms.map((item, index) => {
              const value = Number(item.value || 0);
              const scale = value / maxValue;
              return (
                <Box
                  key={`${item.text}-${index}`}
                  sx={{
                    fontSize: `${0.72 + scale * 1.05}rem`,
                    fontWeight: 700,
                    lineHeight: 1,
                    color: pieColors[index % pieColors.length],
                    px: 0.6,
                    py: 0.35,
                    borderRadius: `${operationalRadii.control}px`,
                    bgcolor: operationalComponentSurfaces.chip[publicMode].background
                  }}
                >
                  {item.text}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    );
  };

  const statCards = [
    {
      icon: ArticleIcon,
      value: formatBigNumber(stats.totalArticles),
      rawValue: stats.totalArticles,
      label: 'Artigos',
      sublabel: stats.period,
      color: '#3b82f6'
    },
    {
      icon: AudienceIcon,
      value: formatBigNumber(stats.totalAudience),
      rawValue: stats.totalAudience,
      label: 'Audiência / Seguidores',
      sublabel: `${stats.withAudience} artigos com dados`,
      color: '#f59e0b'
    },
    {
      icon: ChartIcon,
      value: formatCurrency(stats.totalValuation),
      rawValue: stats.totalValuation,
      label: 'Valoração Total',
      sublabel: 'estimativa de clipping',
      color: '#22c55e'
    }
  ];

  const dashboardKpiCards = [
    {
      title: 'Total de Artigos',
      value: stats.totalArticles || 0,
      icon: ArticleIcon,
      color: '#1976d2'
    },
    {
      title: 'Tier',
      value: tier1Total + tier2Total + tier3Total,
      icon: TierIcon,
      color: '#9e9e9e',
      breakdown: [
        { label: 'Tier 1', value: tier1Total, color: '#4caf50' },
        { label: 'Tier 2', value: tier2Total, color: '#ff9800' },
        { label: 'Tier 3', value: tier3Total, color: '#9e9e9e' },
      ],
    },
    {
      title: 'Audiência',
      value: (audienceDaily + socialViewsTotal) || stats.totalAudience || 0,
      icon: Visibility,
      color: '#3b82f6',
      breakdown: [
        { label: 'Visitas Dia', value: audienceDaily, color: '#0288d1' },
        { label: 'Visualizações', value: socialViewsTotal, color: '#f59e0b' },
      ],
    },
    {
      title: 'Visitas Online',
      value: audienceMonthly,
      icon: Visibility,
      color: '#a855f7',
      breakdown: [
        { label: 'Mês', value: audienceMonthly, color: '#a855f7' },
        { label: 'Dia', value: audienceDaily, color: '#0288d1' },
      ],
      helper: visitsCorrespondence ? `Correspondência: ${visitsCorrespondence}` : undefined,
    },
    {
      title: 'Visualizações',
      value: socialViewsTotal,
      icon: Visibility,
      color: '#f59e0b',
      breakdown: [{ label: 'Total', value: socialViewsTotal, color: '#f59e0b' }],
      helper: socialViewsCorrespondence ? `Correspondência: ${socialViewsCorrespondence}` : undefined,
    },
    {
      title: 'Seguidores',
      value: followersTotal,
      icon: GroupsIcon,
      color: '#06b6d4',
      breakdown: [{ label: 'Perfis únicos', value: followersTotal, color: '#06b6d4' }],
      helper: followersCorrespondence ? `Correspondência: ${followersCorrespondence}` : undefined,
    },
    {
      title: 'Redes Sociais',
      value: socialTotal,
      icon: ThumbUp,
      color: '#ec4899',
      breakdown: [
        { label: 'Curtidas', value: socialLikes, color: '#ec4899' },
        { label: 'Comentários', value: socialComments, color: '#8b5cf6' },
        { label: 'Compartilh.', value: socialShares, color: '#06b6d4' },
        { label: 'Salvos', value: socialSaves, color: '#22c55e' },
      ],
    },
    {
      title: 'Valoração',
      value: valuationTotalBrl,
      icon: TrendingUp,
      color: '#2e7d32',
      breakdown: [
        { label: 'R$', value: valuationTotalBrl, color: '#2e7d32', format: 'currencyBrl' },
        { label: 'US$', value: valuationTotalUsd, color: '#22c55e', format: 'currencyUsd' },
      ],
      helper: usdRate ? `Dólar médio: R$ ${usdRate.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}` : undefined,
    },
    {
      title: 'Sentimento',
      value: sentimentPositive + sentimentNeutral + sentimentNegative,
      icon: SentimentSatisfied,
      color: '#9c27b0',
      breakdown: [
        { label: 'Positivo', value: sentimentPositive, color: '#4caf50' },
        { label: 'Neutro', value: sentimentNeutral, color: '#ff9800' },
        { label: 'Negativo', value: sentimentNegative, color: '#f44336' },
      ],
    },
  ];

  /* ── RENDER ── */
  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: shellBg,
        width: '100%',
        maxWidth: '100vw',
        overflowX: 'hidden',
        '--wl-primary': brandColor,
        '--wl-border': brandBorder,
        '--wl-surface': brandSurface,
        '--wl-soft': brandSoft,
        background: shellBg
      }}
    >
      {/* ── AppBar Fixa ── */}
      <Box
        ref={stickyHeaderRef}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          boxShadow: scrolled ? operationalElevation.surface : operationalElevation.none
        }}
      >
      <AppBar 
        position="static" 
        elevation={0}
        sx={{ 
          bgcolor: 'var(--wl-primary)',
          borderBottom: '1px solid',
          borderColor: brandContrastMuted,
          transition: `box-shadow ${operationalMotion.standard}, background-color ${operationalMotion.standard}`,
          backdropFilter: 'none',
          backgroundImage: 'none',
          backgroundColor: brandHeaderBg,
          '& .MuiToggleButton-root': {
            borderColor: brandContrastMuted,
            color: brandContrastSoft,
            borderRadius: `${operationalRadii.control}px !important`,
            backgroundColor: brandContrastText === '#ffffff' ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.30)'
          },
          '& .MuiToggleButton-root:hover': {
            backgroundColor: brandContrastHover
          },
          '& .MuiToggleButton-root.Mui-selected': {
            bgcolor: selectedControlBg,
            color: selectedControlColor,
            borderColor: selectedControlBg
          },
          '& .MuiToggleButton-root.Mui-selected:hover': {
            bgcolor: selectedControlBg
          },
          '& .MuiIconButton-root': {
            color: brandContrastSoft
          },
          '& .MuiIconButton-root:hover': {
            bgcolor: brandContrastHover
          }
        }}
      >
        <Toolbar sx={{ minHeight: '64px !important', py: 0, gap: 1.5, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, flex: 1 }}>
            {brandLogo ? (
              <Box
                component="img"
                src={brandLogo}
                alt={brand?.name || 'Logo da marca'}
                sx={{ width: 180, height: 'auto', maxHeight: 72, objectFit: 'contain', flexShrink: 0, ml: -1 }}
              />
            ) : (
              <MensureLogo style={{ width: '180px', height: '72px', flexShrink: 0, marginLeft: '-8px' }} />
            )}

            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  letterSpacing: 0,
                  lineHeight: 1.05,
                  color: headerTitleColor,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {brand.name}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  color: headerSubtitleColor,
                  fontSize: '0.72rem',
                  lineHeight: 1,
                  mt: 0.1
                }}
              >
                Dashboard de resultados, analises e monitoramento
              </Typography>
            </Box>
          </Box>

          {/* Filtros de período */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            <ToggleButtonGroup
              value={customDateRange ? null : days}
              exclusive
              onChange={handlePeriodChange}
              size="small"
              sx={{
                  '& .MuiToggleButton-root': {
                    px: 1, py: 0.25, fontSize: '0.65rem', fontWeight: 600,
                    border: '1px solid',
                    borderColor: brandContrastMuted,
                    color: customDateRange ? brandContrastMuted : brandContrastSoft,
                    backgroundColor: brandContrastText === '#ffffff' ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.26)',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: brandContrastHover
                    },
                    '&.Mui-selected': {
                      bgcolor: selectedControlBg,
                      color: selectedControlColor,
                      borderColor: selectedControlBg
                    },
                    '&.Mui-selected:hover': {
                      bgcolor: selectedControlBg
                    }
                }
              }}
            >
              {PERIOD_OPTIONS.map(opt => (
                <ToggleButton key={opt.value} value={opt.value}>{opt.label}</ToggleButton>
              ))}
            </ToggleButtonGroup>
            
            <IconButton 
              onClick={() => setShowDatePicker(true)}
              size="small"
              sx={{ color: customDateRange ? selectedControlBg : brandContrastSoft }}
            >
              <CalendarIcon fontSize="small" />
            </IconButton>
          </Box>
          
          {/* Botão Dashboard */}
          <ToggleButtonGroup
            value={publicTheme}
            exclusive
            onChange={handlePublicThemeChange}
            size="small"
            aria-label="Tema do dashboard publico"
            sx={{
              ml: 0.5,
              flexShrink: 0,
              '& .MuiToggleButton-root': {
                px: 0.75,
                py: 0.25,
                minWidth: 32,
                border: '1px solid',
                borderColor: brandContrastMuted,
                color: brandContrastSoft,
                backgroundColor: brandContrastText === '#ffffff' ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.26)',
                '&:hover': {
                  backgroundColor: brandContrastHover
                },
                '&.Mui-selected': {
                  bgcolor: selectedControlBg,
                  color: selectedControlColor,
                  borderColor: selectedControlBg
                },
                '&.Mui-selected:hover': {
                  bgcolor: selectedControlBg
                }
              }
            }}
          >
            <ToggleButton value="light" aria-label="Tema claro">
              <LightModeIcon sx={{ fontSize: 16 }} />
            </ToggleButton>
            <ToggleButton value="escuro" aria-label="Tema escuro">
              <DarkModeIcon sx={{ fontSize: 16 }} />
            </ToggleButton>
          </ToggleButtonGroup>

          <IconButton
            onClick={() => setChartsModalOpen(true)}
            size="small"
            sx={{
              color: showCharts ? selectedControlBg : brandContrastSoft,
              ml: 1,
              bgcolor: 'transparent'
            }}
          >
            <DashboardIcon fontSize="small" />
          </IconButton>
        </Toolbar>
      </AppBar>

      {availableTags.length > 0 && (
        <Box sx={{
          bgcolor: 'var(--wl-primary)',
          backgroundImage: 'none',
          borderTop: 'none',
          borderBottom: '1px solid',
          borderBottomColor: brandContrastMuted,
          px: { xs: 2, sm: 3, md: 4, lg: 5 },
          py: 0.75,
          transition: 'box-shadow 0.2s ease, background-color 0.2s ease'
        }}>
          <Box sx={{
            display: 'flex',
            gap: 0.6,
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>
            {availableTags.map((tag) => (
              <Chip
                key={tag._id}
                label={tag.name}
                onClick={() => handleTagToggle(tag._id)}
                variant={selectedTagIds.includes(tag._id) ? 'filled' : 'outlined'}
                size="small"
                icon={selectedTagIds.includes(tag._id) ? <CheckIcon sx={{ fontSize: '0.9rem !important' }} /> : undefined}
                sx={{
                  height: 25,
                  fontWeight: 650,
                  fontSize: operationalFontSizes.xs,
                  cursor: 'pointer',
                  borderRadius: 999,
                  backdropFilter: 'none',
                  '& .MuiChip-label': { px: 1 },
                  '& .MuiChip-icon': { color: 'inherit', ml: 0.75 },
                  ...(selectedTagIds.includes(tag._id)
                    ? {
                        bgcolor: selectedControlBg,
                        color: selectedControlColor,
                        borderColor: selectedControlBg,
                        boxShadow: operationalElevation.none,
                        '&:hover': { bgcolor: selectedControlBg }
                      }
                    : {
                        bgcolor: brandContrastText === '#ffffff' ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.32)',
                        color: brandContrastText,
                        borderColor: brandContrastMuted,
                        '&:hover': {
                          bgcolor: brandContrastHover,
                          borderColor: brandContrastText === '#ffffff' ? 'rgba(255,255,255,0.42)' : 'rgba(17,24,39,0.32)'
                        }
                      })
                }}
              />
            ))}
          </Box>
        </Box>
      )}
      </Box>

      <Box sx={{ height: stickyHeaderHeight }} />

      <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3, md: 4, lg: 5 }, py: 2, boxSizing: 'border-box', width: '100%', maxWidth: '100vw' }}>
        {/* ── Seção de Gráficos (expandível) ── */}
        {showCharts && (
          <Box sx={{ mb: 3 }}>
            {/* Stats Cards */}
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              {statCards.map((card) => {
                const Icon = card.icon;
                return (
                  <Grid item xs={4} key={card.label}>
                    <Box sx={{
                      ...sectionPanelSx,
                      textAlign: 'center',
                      borderColor: panelBorder
                    }}>
                      <Icon sx={{ fontSize: 20, color: 'var(--wl-primary)', mb: 0.5 }} />
                      <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--wl-primary)', lineHeight: 1 }}>
                        {card.value}
                      </Typography>
                      <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', fontWeight: 600 }}>
                        {card.label}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}

        {/* ── Tags Fixas (Sticky) ── */}
        {/* ── Footer ── */}
        <Box sx={{ display: 'none' }}>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.78rem' }}>
            Dados gerenciados por <strong style={{ color: 'var(--wl-primary)' }}>Mensure Monitor</strong>
          </Typography>
        </Box>
      </Container>

      {/* Modal de Detalhes do Artigo (estilo página pública completa) */}
      <PublicArticleModal
        articleId={selectedArticle?._id}
        open={articleModalOpen}
        onClose={handleCloseArticleModal}
        onPrev={handlePrevArticle}
        onNext={handleNextArticle}
        hasPrev={currentArticleIndex > 0}
        hasNext={currentArticleIndex < filteredArticles.length - 1}
      />

      {/* Modal de Gráficos */}
      <Dialog
        open={chartsModalOpen}
        onClose={() => setChartsModalOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            borderRadius: `${operationalRadii.modal}px`,
            border: '1px solid',
            borderColor: panelBorder,
            boxShadow: operationalElevation.modal,
            maxHeight: '90vh',
            width: 'min(1480px, calc(100vw - 48px))'
          }
        }}
      >
        <DialogContent
          sx={{
            p: 3,
            bgcolor: panelBg,
            '& .MuiToggleButtonGroup-root': toggleButtonGroupSx,
            '& .MuiDivider-root': { borderColor: panelBorder },
            '& .recharts-cartesian-grid line': { stroke: chartGridStroke },
            '& .recharts-cartesian-axis line, & .recharts-cartesian-axis-tick line': { stroke: chartAxisStroke },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6" fontWeight={700}>Dashboard</Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">Métrica:</Typography>
                <ToggleButtonGroup
                  value={chartMetric}
                  exclusive
                  onChange={(_, v) => v && setChartMetric(v)}
                  size="small"
                  sx={toggleButtonGroupSx}
                >
                  <ToggleButton value="articles">Pub.</ToggleButton>
                  <ToggleButton value="audience">Audiência</ToggleButton>
                  <ToggleButton value="valuation">Valoração (AVE)</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">Tipo:</Typography>
                <ToggleButtonGroup
                  value={chartType}
                  exclusive
                  onChange={(_, v) => v && setChartType(v)}
                  size="small"
                  sx={toggleButtonGroupSx}
                >
                  <ToggleButton value="bar">Barras</ToggleButton>
                  <ToggleButton value="pie">Rosca</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <IconButton size="small" onClick={() => setChartsModalOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          
          {/* Stats Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {dashboardKpiCards.map((card) => {
              const Icon = card.icon;
              const formatValue = (v, fmt) => {
                if (fmt === 'currencyBrl') return `R$ ${formatBigNumber(v)}`;
                if (fmt === 'currencyUsd') return `US$ ${formatBigNumber(v)}`;
                if (fmt === 'currency') return formatCurrency(v);
                return formatBigNumber(v);
              };
              return (
                <Grid item xs={12} sm={6} md={4} lg={2} key={card.title}>
                  <Box sx={{
                    ...sectionPanelSx,
                    minHeight: 150,
                    height: '100%',
                    boxSizing: 'border-box'
                  }}>
                    <Icon sx={{ fontSize: 32, color: card.color, mb: 1.25 }} />
                    {card.breakdown ? (
                      <Box sx={{ display: 'grid', gap: 0.45, mb: 1 }}>
                        {card.breakdown.map((item) => (
                          <Box key={item.label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                            <Typography sx={{ fontSize: '0.75rem', color: item.color, fontWeight: 800, lineHeight: 1.2 }}>
                              {item.label}
                            </Typography>
                            <Typography sx={{ fontSize: '0.75rem', color: 'text.primary', fontWeight: 800, lineHeight: 1.2 }}>
                              {formatValue(item.value, item.format)}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: 'text.primary', lineHeight: 1, mb: 1 }}>
                        {card.format === 'currency' ? formatCurrency(card.value) : formatBigNumber(card.value)}
                      </Typography>
                    )}
                    <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', fontWeight: 600 }}>
                      {card.title}
                    </Typography>
                    {card.helper && (
                      <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', mt: 0.25, lineHeight: 1.3 }}>
                        {card.helper}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
          

          {/* ──── SmartScore ───────────────────────────────────────────────── */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" fontWeight={750} sx={{ mb: 2, letterSpacing: 0 }}>SmartScore™</Typography>

            {!smartScoreEnabled ? (
              <Box sx={{ ...sectionPanelSx, p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">SmartScore sem dados no período selecionado. Verifique se há análises de qualidade nos artigos.</Typography>
              </Box>
            ) : (
              <>
                {/* Linha 1: Gauge + Score + Donut + Legenda */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  {/* Gauge + Score central */}
                  <Grid item xs={12} md={5} lg={4}>
                    <Box sx={{ ...sectionPanelSx, p: 2.5, height: '100%' }}>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'text.secondary', mb: 1.5 }}>Score Global</Typography>
                      {/* Barra gradiente tipo gauge */}
                      <Box sx={{ position: 'relative', mb: 2 }}>
                        <Box sx={{
                          height: 16,
                          borderRadius: operationalRadii.pill,
                          background: 'linear-gradient(90deg, #b71c1c 0%, #d32f2f 12%, #ef6c00 25%, #f59e0b 37%, #c0ca33 50%, #7cb342 62%, #4caf50 75%, #2e7d32 100%)',
                          position: 'relative',
                          overflow: 'visible',
                          mb: 0.5
                        }}>
                          {/* Ponteiro da meta */}
                          <Box sx={{
                            position: 'absolute',
                            left: `${smartScoreTargetPercent}%`,
                            top: -5,
                            width: 2,
                            height: 28,
                            bgcolor: publicText.muted,
                            transform: 'translateX(-50%)',
                            zIndex: 1,
                            borderRadius: 1
                          }} />
                          {/* Ponteiro da nota atual */}
                          <Box sx={{
                            position: 'absolute',
                            left: `${smartScorePercent}%`,
                            top: -8,
                            transform: 'translateX(-50%)',
                            zIndex: 2
                          }}>
                            <Box sx={{
                              width: 0, height: 0,
                              borderLeft: '7px solid transparent',
                              borderRight: '7px solid transparent',
                              borderTop: `10px solid ${smartScoreColor}`
                            }} />
                          </Box>
                        </Box>
                        {/* Labels eixo */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography sx={{ fontSize: '0.65rem', color: '#b71c1c', fontWeight: 700 }}>-10</Typography>
                          <Typography sx={{ fontSize: '0.65rem', color: '#f59e0b', fontWeight: 700 }}>0</Typography>
                          <Typography sx={{ fontSize: '0.65rem', color: '#2e7d32', fontWeight: 700 }}>+10</Typography>
                        </Box>
                      </Box>
                      {/* Score numérico */}
                      <Box sx={{ textAlign: 'center', mb: 2 }}>
                        <Typography sx={{ fontSize: '3.15rem', fontWeight: 800, color: smartScoreColor, lineHeight: 1, letterSpacing: 0 }}>
                          {formatSignedScore(smartScoreValue)}
                        </Typography>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: SMART_SCORE_STATUS_COLORS[smartScoreGlobal.status] || '#f59e0b', mt: 0.25 }}>
                          {smartScoreGlobal.status || 'Alerta'}
                        </Typography>
                        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: 0.5 }}>
                          {smartScoreGlobal.count?.toLocaleString('pt-BR') || '0'} artigos analisados
                        </Typography>
                      </Box>
                      {/* Meta */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', fontWeight: 600 }}>Meta</Typography>
                        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                          <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: SMART_SCORE_STATUS_COLORS[smartScoreGlobal.targetStatus] || '#4caf50' }}>
                            {smartScoreGlobal.targetStatus}
                          </Typography>
                          <Typography sx={{ fontSize: '0.82rem', fontWeight: 900, color: 'text.primary' }}>
                            {formatSignedScore(smartScoreTargetValue)}
                          </Typography>
                        </Box>
                      </Box>
                      {/* Barra de progresso rumo à meta */}
                      <Box sx={{ bgcolor: panelHover, borderRadius: operationalRadii.pill, height: 6, overflow: 'hidden', mb: 0.5 }}>
                        <Box sx={{
                          width: `${Math.min(100, smartScoreTargetProgressPercent)}%`,
                          height: '100%',
                          borderRadius: 4,
                          bgcolor: smartScoreGlobal.targetReached ? '#2e7d32' : smartScoreColor,
                          transition: 'width 0.6s ease'
                        }} />
                      </Box>
                      <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>
                        {smartScoreGlobal.targetReached
                          ? '✔ Meta atingida'
                          : `Distância: ${formatSignedScore(smartScoreGlobal.targetGap || 0)}`}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Donut de distribuição por escala */}
                  <Grid item xs={12} md={4} lg={4}>
                    <Box sx={{ ...sectionPanelSx, p: 2.5, height: '100%' }}>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'text.secondary', mb: 1.5 }}>Distribuição por Faixa</Typography>
                      {smartScoreStatusTotal > 0 ? (
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                          <Box sx={{ width: 160, height: 160, flexShrink: 0 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie
                                  data={smartScoreLegend.filter(i => i.count > 0).map(i => ({ name: i.label, value: i.count }))}
                                  cx="50%" cy="50%"
                                  innerRadius={42} outerRadius={74}
                                  dataKey="value" nameKey="name"
                                >
                                  {smartScoreLegend.filter(i => i.count > 0).map((item) => (
                                    <Cell key={item.label} fill={SMART_SCORE_STATUS_COLORS[item.label] || '#9e9e9e'} />
                                  ))}
                                </Pie>
                                <Tooltip
                                  contentStyle={{ ...chartTooltipSx, fontSize: 11 }}
                                  formatter={(value, name) => [value, name]}
                                />
                              </PieChart>
                            </ResponsiveContainer>
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                            {[...smartScoreLegend].reverse().map((item) => (
                              <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: SMART_SCORE_STATUS_COLORS[item.label] || '#9e9e9e', flexShrink: 0 }} />
                                <Typography sx={{ fontSize: '0.7rem', flex: 1, color: item.count > 0 ? 'text.primary' : 'text.disabled' }}>{item.label}</Typography>
                                <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, minWidth: 24, textAlign: 'right', color: item.count > 0 ? 'text.primary' : 'text.disabled' }}>{item.count}</Typography>
                                {smartScoreStatusTotal > 0 && (
                                  <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', minWidth: 36, textAlign: 'right' }}>
                                    {formatPercent((item.count / smartScoreStatusTotal) * 100)}
                                  </Typography>
                                )}
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      ) : (
                        <Box sx={{ textAlign: 'center', py: 3 }}><Typography variant="caption" color="text.disabled">Sem dados de distribuição</Typography></Box>
                      )}
                    </Box>
                  </Grid>

                  {/* Contadores sumarários */}
                  <Grid item xs={12} md={3} lg={4}>
                    <Box sx={{ ...sectionPanelSx, p: 2.5, height: '100%' }}>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'text.secondary', mb: 1.5 }}>Resumo do Período</Typography>
                      <Box sx={{ display: 'grid', gap: 1.25 }}>
                        {[
                          { label: 'Positivos (acima de +2)', value: smartScoreGlobal.positiveCount || 0, color: '#4caf50' },
                          { label: 'Em Alerta (-2 a +2)', value: smartScoreGlobal.alertCount || 0, color: '#f59e0b' },
                          { label: 'Negativos (abaixo de -2)', value: smartScoreGlobal.negativeCount || 0, color: '#d32f2f' },
                        ].map(item => (
                          <Box key={item.label}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.35 }}>
                              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{item.label}</Typography>
                              <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: item.color }}>{item.value.toLocaleString('pt-BR')}</Typography>
                            </Box>
                            <Box sx={{ bgcolor: panelHover, borderRadius: operationalRadii.pill, height: 5, overflow: 'hidden' }}>
                              <Box sx={{ width: `${smartScoreGlobal.count > 0 ? (item.value / smartScoreGlobal.count) * 100 : 0}%`, height: '100%', bgcolor: item.color, borderRadius: 4, transition: 'width 0.4s' }} />
                            </Box>
                          </Box>
                        ))}
                      </Box>
                      <Divider sx={{ my: 1.5 }} />
                      <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mb: 0.5 }}>Score Médio</Typography>
                      <Typography sx={{ fontSize: '1.4rem', fontWeight: 900, color: getSmartScoreColor(smartScoreGlobal.averageScore), lineHeight: 1 }}>
                        {formatSignedScore(smartScoreGlobal.averageScore)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Linha 2: Composição por Indicador */}
                {smartScoreComponents.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'text.secondary', mb: 1.5, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Composição por Indicador</Typography>
                    <Grid container spacing={2}>
                      {smartScoreComponents.map((component) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={component.key}>
                          <Box sx={{ ...sectionPanelSx, height: '100%' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.25 }}>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>{component.label}</Typography>
                                <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', mt: 0.2 }}>{component.count} artigos • peso {component.averageWeight.toFixed(1)}%</Typography>
                              </Box>
                              <Typography sx={{ fontSize: '1.35rem', fontWeight: 900, color: getIndicatorScoreColor(component.averageScore), lineHeight: 1, ml: 1 }}>
                                {component.averageScore.toFixed(1)}
                              </Typography>
                            </Box>
                            {/* Barra de progresso do indicador */}
                            <Box sx={{ bgcolor: panelHover, borderRadius: operationalRadii.pill, height: 6, overflow: 'hidden', mb: 1 }}>
                              <Box sx={{ width: `${(component.averageScore / 10) * 100}%`, height: '100%', bgcolor: getIndicatorScoreColor(component.averageScore), borderRadius: 4, transition: 'width 0.4s' }} />
                            </Box>
                            {/* Chips de distribuição dos elementos */}
                            {component.elements.length > 0 && (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {component.elements.slice(0, 4).map((el, elIdx) => (
                                  <Box key={el.label} sx={{
                                    display: 'inline-flex', alignItems: 'center', gap: 0.4,
                                    px: 0.75, py: 0.2, borderRadius: 1,
                                    bgcolor: `${getKpiElementColor(component.key, el, elIdx)}22`,
                                    border: '1px solid',
                                    borderColor: `${getKpiElementColor(component.key, el, elIdx)}55`
                                  }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: getKpiElementColor(component.key, el, elIdx) }} />
                                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: 'text.secondary' }}>{el.label}</Typography>
                                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 900, color: 'text.primary' }}>{el.count}</Typography>
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                {/* Linha 3: Diagnóstico por Faixa */}
                {smartScoreLegend.some(i => i.count > 0) && (
                  <Box sx={{ mb: 1 }}>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'text.secondary', mb: 1.5, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Diagnóstico por Faixa de Resultado</Typography>
                    <Grid container spacing={1.5}>
                      {[...smartScoreLegend].reverse().filter(i => i.count > 0).map((item) => {
                        const { weak, strong } = getStatusInsightItems(item);
                        const statusColor = SMART_SCORE_STATUS_COLORS[item.label] || '#9e9e9e';
                        return (
                          <Grid item xs={12} sm={6} md={4} lg={3} key={item.label}>
                            <Box sx={{
                              ...sectionPanelSx,
                              p: 1.75,
                              border: '1px solid',
                              borderColor: `${statusColor}40`,
                              height: '100%'
                            }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: statusColor }}>{item.label}</Typography>
                                <Box sx={{ textAlign: 'right' }}>
                                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 900, color: 'text.primary' }}>{item.count}</Typography>
                                  <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled' }}>{formatSmartScoreRange(item)}</Typography>
                                </Box>
                              </Box>
                              {weak.length > 0 && (
                                <Box sx={{ mb: 0.75 }}>
                                  <Typography sx={{ fontSize: '0.62rem', color: '#d32f2f', fontWeight: 700, mb: 0.35 }}>⚠ Pontos fracos</Typography>
                                  {weak.map(c => (
                                    <Box key={c.key} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                      <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary' }}>{c.label}</Typography>
                                      <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: getIndicatorScoreColor(c.averageScore) }}>{c.averageScore?.toFixed(1)}</Typography>
                                    </Box>
                                  ))}
                                </Box>
                              )}
                              {strong.length > 0 && (
                                <Box>
                                  <Typography sx={{ fontSize: '0.62rem', color: '#4caf50', fontWeight: 700, mb: 0.35 }}>✔ Pontos fortes</Typography>
                                  {strong.map(c => (
                                    <Box key={c.key} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                      <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary' }}>{c.label}</Typography>
                                      <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: getIndicatorScoreColor(c.averageScore) }}>{c.averageScore?.toFixed(1)}</Typography>
                                    </Box>
                                  ))}
                                </Box>
                              )}
                              {weak.length === 0 && strong.length === 0 && (
                                <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled' }}>Sem dados de componentes</Typography>
                              )}
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                )}
              </>
            )}
          </Box>

          {/* Dashboard interno replicado */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6} lg={3}>
              {renderCountChart('Plataformas', platformCountChart, { type: 'bar', height: 250, color: '#0288d1' })}
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              {renderCountChart('Categorias', categoryChart, { type: 'pie', height: 250 })}
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              {renderCountChart('Abrangência', scopeChart, { type: 'pie', height: 250 })}
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              {renderCountChart('Top Estados', ufChart, { type: 'bar', height: 250, color: '#673ab7' })}
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} lg={8}>
              <Box sx={chartBoxSx}>
                <Typography sx={{ fontSize: '1rem', fontWeight: 700, mb: 2 }}>
                  Artigos ao Longo do Tempo
                </Typography>
                {internalTimelineData.length === 0 ? renderEmptyChart() : (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={internalTimelineData}>
                      <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} />
                      <XAxis dataKey="date" stroke={chartAxisStroke} fontSize={11} tickLine={false} />
                      <YAxis stroke={chartAxisStroke} fontSize={11} tickLine={false} axisLine={false} />
                      <Tooltip
                        contentStyle={chartTooltipSx}
                        formatter={(value) => [formatNumberBR(value), 'Artigos por Dia']}
                      />
                      <Line type="monotone" dataKey="value" stroke="#1976d2" strokeWidth={3} dot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              {renderCountChart('Distribuição de Sentimento', sentimentChart, { type: 'pie', height: 300 })}
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} lg={8}>
              <Box sx={{
                '& .MuiCard-root': {
                  height: '100%',
                  bgcolor: panelBg,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: 'none'
                }
              }}>
                <WordCloudCard
                  wordCloud={wordCloud}
                  tagStats={keywordsCloud}
                  keywordsCloud={keywordsCloud}
                  wordLimit={publicWordLimit}
                  setWordLimit={setPublicWordLimit}
                  cloudType={publicCloudType}
                  setCloudType={setPublicCloudType}
                  contextFilter={publicWordContext}
                  setContextFilter={setPublicWordContext}
                  loading={false}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              {renderCountChart('Distribuição de Exposição', exposureChart, { type: 'pie', height: 300 })}
            </Grid>
          </Grid>

          {/* Gráfico de Evolução */}
          {dailyEvolution && dailyEvolution.length > 1 && (
            <Box sx={sectionPanelSx}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>
                  Evolução - {evolutionGroupBy === 'month' ? 'Mensal' : 'Diária'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                  <ToggleButtonGroup
                    value={evolutionMetric || chartMetric}
                    exclusive
                    onChange={(_, v) => v && setEvolutionMetric(v)}
                    size="small"
                    sx={{
                      '& .MuiToggleButton-root': {
                        px: 1, py: 0.25, fontSize: '0.65rem', fontWeight: 600,
                        border: '1px solid', borderColor: 'divider',
                        color: 'text.secondary',
                        textTransform: 'none',
                      '&.Mui-selected': { bgcolor: 'var(--wl-primary)', color: '#fff', borderColor: 'var(--wl-primary)' }
                      }
                    }}
                  >
                    <ToggleButton value="articles">Pub.</ToggleButton>
                    <ToggleButton value="audience">Audiência</ToggleButton>
                    <ToggleButton value="valuation">Valoração (AVE)</ToggleButton>
                  </ToggleButtonGroup>
                  <ToggleButtonGroup
                    value={evolutionGroupBy}
                    exclusive
                    onChange={(_, v) => v && setEvolutionGroupBy(v)}
                    size="small"
                    sx={{
                      '& .MuiToggleButton-root': {
                        px: 1, py: 0.25, fontSize: '0.65rem', fontWeight: 600,
                        border: '1px solid', borderColor: 'divider',
                        color: 'text.secondary',
                        textTransform: 'none',
                      '&.Mui-selected': { bgcolor: 'var(--wl-primary)', color: '#fff', borderColor: 'var(--wl-primary)' }
                      }
                    }}
                  >
                    <ToggleButton value="day">Dia</ToggleButton>
                    <ToggleButton value="month">Mês</ToggleButton>
                  </ToggleButtonGroup>
                  {brandVsCompetitors.length > 0 && (
                    <ToggleButtonGroup
                      value={evolutionMode}
                      exclusive
                      onChange={(_, v) => v && setEvolutionMode(v)}
                      size="small"
                      sx={{
                        '& .MuiToggleButton-root': {
                          px: 1, py: 0.25, fontSize: '0.65rem', fontWeight: 600,
                          border: '1px solid', borderColor: 'divider',
                          color: 'text.secondary',
                          textTransform: 'none',
                          '&.Mui-selected': { bgcolor: 'var(--wl-primary)', color: '#fff', borderColor: 'var(--wl-primary)' }
                        }
                      }}
                    >
                      <ToggleButton value="brand">Minha Marca</ToggleButton>
                      <ToggleButton value="all">+ Concorrentes</ToggleButton>
                    </ToggleButtonGroup>
                  )}
                </Box>
              </Box>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={evolutionMode === 'all' && competitorEvolution.length > 0 ? getCompetitorEvolutionData() : getEvolutionData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} />
                  <XAxis dataKey="date" stroke={chartAxisStroke} fontSize={11} tickLine={false} />
                  <YAxis stroke={chartAxisStroke} fontSize={11} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={chartTooltipSx} formatter={(value, name) => [formatNumberBR(value), name]} />
                  {evolutionMode === 'all' && competitorEvolution.length > 0 ? (
                    brandVsCompetitors.map((entity, index) => (
                      <Line
                        key={entity.name}
                        type="monotone"
                        dataKey={entity.name}
                        name={entity.name}
                        stroke={getCompetitorColor(entity.name, entity.isBrand, index)}
                        strokeWidth={2}
                        dot={{ r: 3 }}
                      />
                    ))
                  ) : (
                    <Line 
                      type="monotone" 
                      dataKey={(evolutionMetric || chartMetric) === 'articles' ? 'articles' : (evolutionMetric || chartMetric) === 'audience' ? 'audience' : 'valuation'} 
                      stroke={(evolutionMetric || chartMetric) === 'articles' ? '#3b82f6' : (evolutionMetric || chartMetric) === 'audience' ? '#f59e0b' : '#22c55e'} 
                      strokeWidth={3}
                      dot={{ r: 4 }}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
              {evolutionMode === 'all' && competitorEvolution.length > 0 && (
                <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                  {brandVsCompetitors.map((entity, index) => (
                    <Box key={entity.name} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 12, height: 3, bgcolor: getCompetitorColor(entity.name, entity.isBrand, index) }} />
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{entity.name}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* Gráfico por Dia da Semana */}
          {dailyEvolution && dailyEvolution.length > 0 && (
            <Box sx={{ ...sectionPanelSx, mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>
                  {weekdayView === 'avg' ? 'Média por Dia da Semana' : 'Total por Dia da Semana'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <ToggleButtonGroup
                    value={weekdayView}
                    exclusive
                    onChange={(_, v) => v && setWeekdayView(v)}
                    size="small"
                    sx={{
                      '& .MuiToggleButton-root': {
                        px: 1, py: 0.25, fontSize: '0.65rem', fontWeight: 600,
                        border: '1px solid', borderColor: 'divider',
                        color: 'text.secondary',
                        textTransform: 'none',
                      '&.Mui-selected': { bgcolor: 'var(--wl-primary)', color: '#fff', borderColor: 'var(--wl-primary)' }
                      }
                    }}
                  >
                    <ToggleButton value="avg">Média</ToggleButton>
                    <ToggleButton value="total">Total</ToggleButton>
                  </ToggleButtonGroup>
                  <ToggleButtonGroup
                    value={(evolutionMetric || chartMetric) === 'articles' ? 'articles' : (evolutionMetric || chartMetric) === 'audience' ? 'audience' : 'valuation'}
                    exclusive
                    onChange={(_, v) => v && setEvolutionMetric(v)}
                    size="small"
                    sx={{
                      '& .MuiToggleButton-root': {
                        px: 1, py: 0.25, fontSize: '0.65rem', fontWeight: 600,
                        border: '1px solid', borderColor: 'divider',
                        color: 'text.secondary',
                        textTransform: 'none',
                      '&.Mui-selected': { bgcolor: 'var(--wl-primary)', color: '#fff', borderColor: 'var(--wl-primary)' }
                      }
                    }}
                  >
                    <ToggleButton value="articles">Pub.</ToggleButton>
                    <ToggleButton value="audience">Audiência</ToggleButton>
                    <ToggleButton value="valuation">Valoração (AVE)</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Box>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={getWeekdayData()} margin={{ left: 10, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} vertical={false} />
                  <XAxis dataKey="name" stroke={chartAxisStroke} fontSize={11} tickLine={false} />
                  <YAxis stroke={chartAxisStroke} fontSize={11} tickLine={false} axisLine={false} />
                  <Tooltip 
                    contentStyle={chartTooltipSx}
                    formatter={(value, name) => [formatNumberBR(value), weekdayView === 'avg' ? (name === 'articles' ? 'Pub. média' : name === 'audience' ? 'Audiência média' : 'Valoração média') : (name === 'articles' ? 'Total Pub.' : name === 'audience' ? 'Total Audiência' : 'Total Valoração')]}
                    labelFormatter={(label, payload) => payload && payload[0] ? payload[0].payload.fullName : label}
                  />
                  <Bar 
                    dataKey={weekdayView === 'avg' 
                      ? ((evolutionMetric || chartMetric) === 'articles' ? 'articles' : (evolutionMetric || chartMetric) === 'audience' ? 'audience' : 'valuation')
                      : ((evolutionMetric || chartMetric) === 'articles' ? 'totalArticles' : (evolutionMetric || chartMetric) === 'audience' ? 'totalAudience' : 'totalValuation')
                    }
                    fill="#3b82f6" 
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}

          {/* Gráfico Comparativo de Plataformas */}
          {sortedPlatforms && sortedPlatforms.length > 0 && (
            <Box sx={{ ...sectionPanelSx, mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>Por Plataforma</Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <ToggleButtonGroup
                    value={platformMetric || chartMetric}
                    exclusive
                    onChange={(_, v) => v && setPlatformMetric(v)}
                    size="small"
                    sx={{
                      '& .MuiToggleButton-root': {
                        px: 1, py: 0.25, fontSize: '0.65rem', fontWeight: 600,
                        border: '1px solid', borderColor: 'divider',
                        color: 'text.secondary',
                        textTransform: 'none',
                      '&.Mui-selected': { bgcolor: 'var(--wl-primary)', color: '#fff', borderColor: 'var(--wl-primary)' }
                      }
                    }}
                  >
                    <ToggleButton value="articles">Pub.</ToggleButton>
                    <ToggleButton value="audience">Audiência</ToggleButton>
                    <ToggleButton value="valuation">Valoração (AVE)</ToggleButton>
                  </ToggleButtonGroup>
                  <ToggleButtonGroup
                    value={platformType || chartType}
                    exclusive
                    onChange={(_, v) => v && setPlatformType(v)}
                    size="small"
                    sx={{
                      '& .MuiToggleButton-root': {
                        px: 1, py: 0.25, fontSize: '0.65rem', fontWeight: 600,
                        border: '1px solid', borderColor: 'divider',
                        color: 'text.secondary',
                        textTransform: 'none',
                      '&.Mui-selected': { bgcolor: 'var(--wl-primary)', color: '#fff', borderColor: 'var(--wl-primary)' }
                      }
                    }}
                  >
                    <ToggleButton value="bar">Barras</ToggleButton>
                    <ToggleButton value="pie">Rosca</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Box>
              {(platformType || chartType) === 'bar' ? (
                <ResponsiveContainer width="100%" height={Math.max(250, sortedPlatforms.length * 45)}>
                  <BarChart 
                    data={sortedPlatforms.map(p => ({
                      platform: p._id,
                      Pub: p.count,
                      Audiência: p.totalAudience,
                      'Valoração (AVE)': p.totalValuation || p.valuation || 0
                    }))}
                    layout="vertical"
                    margin={{ left: 20, right: 30 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} horizontal={false} />
                    <XAxis type="number" stroke={chartAxisStroke} fontSize={11} tickLine={false} />
                    <YAxis type="category" dataKey="platform" stroke={chartAxisStroke} fontSize={11} tickLine={false} width={80} />
                    <Tooltip 
                      contentStyle={chartTooltipSx}
                      formatter={(value, name) => [formatNumberBR(value), name]}
                    />
                    <Bar dataKey={(platformMetric || chartMetric) === 'articles' ? 'Pub' : (platformMetric || chartMetric) === 'audience' ? 'Audiência' : 'Valoração (AVE)'} fill="#3b82f6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'center', minHeight: 180 }}>
                  <Box sx={{ width: 180, height: 180 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={sortedPlatforms.map(p => ({
                            name: p._id,
                            value: (platformMetric || chartMetric) === 'articles' ? p.count : (platformMetric || chartMetric) === 'audience' ? p.totalAudience : (p.totalValuation || p.valuation || 0)
                          }))}
                          cx="50%"
                          cy="50%"
                          innerRadius={45}
                          outerRadius={80}
                          dataKey="value"
                          nameKey="name"
                        >
                          {sortedPlatforms.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={chartTooltipSx} />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                  <Box sx={{ flex: 1, maxWidth: 300, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {(() => {
                      const total = sortedPlatforms.reduce((sum, p) => {
                        const metricKey = platformMetric || chartMetric;
                        return sum + (metricKey === 'audience' ? p.totalAudience : metricKey === 'valuation' ? (p.totalValuation || p.valuation || 0) : p.count);
                      }, 0);
                      return sortedPlatforms.map((p, index) => {
                        const metricKey = platformMetric || chartMetric;
                        const value = metricKey === 'audience' ? p.totalAudience : metricKey === 'valuation' ? (p.totalValuation || p.valuation || 0) : p.count;
                        const pct = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                        return (
                          <Box key={p._id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 12, height: 12, bgcolor: pieColors[index % pieColors.length], borderRadius: 0.5, flexShrink: 0 }} />
                            <Typography variant="caption" sx={{ color: 'text.secondary', flex: 1 }}>{p._id}</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary', minWidth: 60, textAlign: 'right' }}>
                              {value.toLocaleString('pt-BR')}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.disabled', minWidth: 50, textAlign: 'right' }}>
                              {pct}%
                            </Typography>
                          </Box>
                        );
                      });
                    })()}
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* Gráfico Comparativo: Coleções (Tags) */}
          {collections && collections.length > 0 && (
            <Box sx={{ ...sectionPanelSx, mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>
                  Comparar Coleções
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  <ToggleButtonGroup
                    value={collectionMetric}
                    exclusive
                    onChange={(_, v) => v && setCollectionMetric(v)}
                    size="small"
                    sx={{
                      '& .MuiToggleButton-root': {
                        px: 1, py: 0.25, fontSize: '0.65rem', fontWeight: 600,
                        border: '1px solid', borderColor: 'divider',
                        color: 'text.secondary',
                        textTransform: 'none',
                      '&.Mui-selected': { bgcolor: 'var(--wl-primary)', color: '#fff', borderColor: 'var(--wl-primary)' }
                      }
                    }}
                  >
                    <ToggleButton value="articles">Pub.</ToggleButton>
                    <ToggleButton value="audience">Audiência</ToggleButton>
                    <ToggleButton value="valuation">Valoração</ToggleButton>
                  </ToggleButtonGroup>
                  <ToggleButtonGroup
                    value={collectionGroupBy}
                    exclusive
                    onChange={(_, v) => v && setCollectionGroupBy(v)}
                    size="small"
                    sx={{
                      '& .MuiToggleButton-root': {
                        px: 1, py: 0.25, fontSize: '0.65rem', fontWeight: 600,
                        border: '1px solid', borderColor: 'divider',
                        color: 'text.secondary',
                        textTransform: 'none',
                      '&.Mui-selected': { bgcolor: 'var(--wl-primary)', color: '#fff', borderColor: 'var(--wl-primary)' }
                      }
                    }}
                  >
                    <ToggleButton value="day">Dia</ToggleButton>
                    <ToggleButton value="month">Mês</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Box>

              {/* Seletor de Coleções */}
              <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {collectionStats.map((col) => (
                  <Chip
                    key={col.name}
                    label={`${col.name} (${col.articles})`}
                    size="small"
                    onClick={() => {
                      setSelectedCollections(prev => 
                        prev.includes(col.name) 
                          ? prev.filter(c => c !== col.name)
                          : [...prev, col.name]
                      );
                    }}
                    sx={{
                      bgcolor: selectedCollections.includes(col.name) ? 'var(--wl-primary)' : 'action.hover',
                      color: selectedCollections.includes(col.name) ? '#fff' : 'text.primary',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: selectedCollections.includes(col.name) ? 'primary.dark' : 'action.selected' }
                    }}
                  />
                ))}
              </Box>

              {/* Resumo das Coleções Selecionadas */}
              {collectionStats.length > 0 && (
                <Box sx={{ mb: 2, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 1 }}>
                  {(selectedCollections.length > 0 ? collectionStats.filter(c => selectedCollections.includes(c.name)) : collectionStats.slice(0, 5)).map((col, index) => {
                    const value = collectionMetric === 'audience' ? col.audience : collectionMetric === 'valuation' ? col.valuation : col.articles;
                    return (
                      <Box key={col.name} sx={{ p: 1, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: getCompetitorColor(col.name, false, index) }} />
                          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>{col.name}</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                          {collectionMetric === 'audience' ? formatBigNumber(col.audience) : collectionMetric === 'valuation' ? formatCurrency(col.valuation) : col.articles}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* Gráfico de Evolução das Coleções */}
              {collectionEvolution && collectionEvolution.length > 0 && (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={getCollectionEvolutionData()}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} />
                    <XAxis dataKey="date" stroke={chartAxisStroke} fontSize={11} tickLine={false} />
                    <YAxis stroke={chartAxisStroke} fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => formatNumberBR(v)} />
                    <Tooltip 
                      contentStyle={chartTooltipSx}
                      formatter={(value, name) => [formatNumberBR(value), name]}
                    />
                    {(selectedCollections.length > 0 ? collectionStats.filter(c => selectedCollections.includes(c.name)) : collectionStats.slice(0, 5)).map((col, index) => (
                      <Line
                        key={col.name}
                        type="monotone"
                        dataKey={col.name}
                        name={col.name}
                        stroke={getCompetitorColor(col.name, false, index)}
                        strokeWidth={2}
                        dot={{ r: 3 }}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Box>
          )}

          {/* Resumo Marca vs Concorrentes */}
          {sortedCompetitors.length > 0 && (
            <Box sx={{ ...sectionPanelSx, mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>Marca vs Concorrentes</Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <ToggleButtonGroup
                    value={competitorMetric || chartMetric}
                    exclusive
                    onChange={(_, v) => v && setCompetitorMetric(v)}
                    size="small"
                    sx={{
                      '& .MuiToggleButton-root': {
                        px: 1, py: 0.25, fontSize: '0.65rem', fontWeight: 600,
                        border: '1px solid', borderColor: 'divider',
                        color: 'text.secondary',
                        textTransform: 'none',
                      '&.Mui-selected': { bgcolor: 'var(--wl-primary)', color: '#fff', borderColor: 'var(--wl-primary)' }
                      }
                    }}
                  >
                    <ToggleButton value="articles">Pub.</ToggleButton>
                    <ToggleButton value="audience">Audiência</ToggleButton>
                    <ToggleButton value="valuation">Valoração (AVE)</ToggleButton>
                  </ToggleButtonGroup>
                  <ToggleButtonGroup
                    value={competitorType || chartType}
                    exclusive
                    onChange={(_, v) => v && setCompetitorType(v)}
                    size="small"
                    sx={{
                      '& .MuiToggleButton-root': {
                        px: 1, py: 0.25, fontSize: '0.65rem', fontWeight: 600,
                        border: '1px solid', borderColor: 'divider',
                        color: 'text.secondary',
                        textTransform: 'none',
                        '&.Mui-selected': { bgcolor: 'var(--wl-primary)', color: '#fff', borderColor: 'var(--wl-primary)' }
                      }
                    }}
                  >
                    <ToggleButton value="bar">Barras</ToggleButton>
                    <ToggleButton value="pie">Rosca</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Box>
              {(competitorType || chartType) === 'bar' ? (
                <ResponsiveContainer width="100%" height={Math.max(200, sortedCompetitors.length * 45)}>
                  <BarChart 
                    data={sortedCompetitors.map((item) => ({
                      name: item.name,
                      Pub: item.articles,
                      Audiência: item.audience,
                      'Valoração (AVE)': item.valuation,
                      isBrand: item.isBrand
                    }))}
                    layout="vertical"
                    margin={{ left: 20, right: 30 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} horizontal={false} />
                    <XAxis type="number" stroke={chartAxisStroke} fontSize={11} tickLine={false} />
                    <YAxis type="category" dataKey="name" stroke={chartAxisStroke} fontSize={11} tickLine={false} width={100} />
                    <Tooltip 
                      contentStyle={chartTooltipSx}
                      formatter={(value, name) => [formatNumberBR(value), name]}
                    />
                    <Bar dataKey={(competitorMetric || chartMetric) === 'articles' ? 'Pub' : (competitorMetric || chartMetric) === 'audience' ? 'Audiência' : 'Valoração (AVE)'} radius={[0, 4, 4, 0]}>
                      {sortedCompetitors.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getCompetitorColor(entry.name, entry.isBrand, index)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'center', minHeight: 180 }}>
                  <Box sx={{ width: 180, height: 180 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={sortedCompetitors.map((item) => ({
                            name: item.name,
                            value: (competitorMetric || chartMetric) === 'articles' ? item.articles : (competitorMetric || chartMetric) === 'audience' ? item.audience : item.valuation,
                            isBrand: item.isBrand
                          }))}
                          cx="50%"
                          cy="50%"
                          innerRadius={45}
                          outerRadius={80}
                          dataKey="value"
                          nameKey="name"
                        >
                          {sortedCompetitors.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={getCompetitorColor(entry.name, entry.isBrand, index)} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={chartTooltipSx} />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                  <Box sx={{ flex: 1, maxWidth: 300 }}>
                    {(() => {
                      const metricKey = competitorMetric || chartMetric;
                      const total = sortedCompetitors.reduce((sum, item) => {
                        return sum + (metricKey === 'audience' ? item.audience : metricKey === 'valuation' ? item.valuation : item.articles);
                      }, 0);
                      return (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {sortedCompetitors.map((item) => {
                            const value = metricKey === 'audience' ? item.audience : metricKey === 'valuation' ? item.valuation : item.articles;
                            const pct = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                            const itemIndex = sortedCompetitors.findIndex(c => c.name === item.name);
                            return (
                              <Box key={item.name} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 12, height: 12, bgcolor: getCompetitorColor(item.name, item.isBrand, itemIndex), borderRadius: 0.5, flexShrink: 0 }} />
                                <Typography variant="caption" sx={{ color: 'text.secondary', flex: 1 }}>{item.name}</Typography>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary', minWidth: 60, textAlign: 'right' }}>
                                  {formatNumberBR(value)}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.disabled', minWidth: 50, textAlign: 'right' }}>
                                  {pct}%
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      );
                    })()}
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* Gráfico de Principais Fontes */}
          {sortedSources && sortedSources.length > 0 && (
            <Box sx={{ ...sectionPanelSx, mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>Principais Fontes</Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <ToggleButtonGroup
                    value={sourceLimit}
                    exclusive
                    onChange={(_, v) => v && setSourceLimit(v)}
                    size="small"
                    sx={{
                      '& .MuiToggleButton-root': {
                        px: 1, py: 0.25, fontSize: '0.65rem', fontWeight: 600,
                        border: '1px solid', borderColor: 'divider',
                        color: 'text.secondary',
                        textTransform: 'none',
                        '&.Mui-selected': { bgcolor: 'var(--wl-primary)', color: '#fff', borderColor: 'var(--wl-primary)' }
                      }
                    }}
                  >
                    <ToggleButton value={10}>10+</ToggleButton>
                    <ToggleButton value={20}>20+</ToggleButton>
                    <ToggleButton value={30}>30+</ToggleButton>
                  </ToggleButtonGroup>
                  <ToggleButtonGroup
                    value={sourceMetric || chartMetric}
                    exclusive
                    onChange={(_, v) => v && setSourceMetric(v)}
                    size="small"
                    sx={{
                      '& .MuiToggleButton-root': {
                        px: 1, py: 0.25, fontSize: '0.65rem', fontWeight: 600,
                        border: '1px solid', borderColor: 'divider',
                        color: 'text.secondary',
                        textTransform: 'none',
                        '&.Mui-selected': { bgcolor: 'var(--wl-primary)', color: '#fff', borderColor: 'var(--wl-primary)' }
                      }
                    }}
                  >
                    <ToggleButton value="articles">Pub.</ToggleButton>
                    <ToggleButton value="audience">Audiência</ToggleButton>
                    <ToggleButton value="valuation">Valoração (AVE)</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Box>
              <ResponsiveContainer width="100%" height={Math.max(250, sortedSources.length * 35)}>
                <BarChart 
                  data={sortedSources.map(s => ({
                    fonte: s._id || 'Desconhecido',
                    Pub: s.count,
                    Audiência: s.totalAudience,
                    'Valoração (AVE)': s.totalValuation
                  }))}
                  layout="vertical"
                  margin={{ left: 20, right: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} horizontal={false} />
                  <XAxis type="number" stroke={chartAxisStroke} fontSize={11} tickLine={false} />
                  <YAxis type="category" dataKey="fonte" stroke={chartAxisStroke} fontSize={10} tickLine={false} width={120} />
                  <Tooltip 
                    contentStyle={chartTooltipSx}
                    formatter={(value, name) => [formatNumberBR(value), name]}
                  />
                    <Bar dataKey={(sourceMetric || chartMetric) === 'articles' ? 'Pub' : (sourceMetric || chartMetric) === 'audience' ? 'Audiência' : 'Valoração (AVE)'} fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
            </Box>
          )}

          {/* Top Tags */}
          {tagStats && tagStats.length > 0 && (
            <Box sx={{ ...sectionPanelSx, mt: 2 }}>
              <Typography sx={{ fontSize: '1rem', fontWeight: 700, mb: 2 }}>Top Tags</Typography>
              <ResponsiveContainer width="100%" height={Math.max(200, tagStats.length * 34)}>
                <BarChart
                  data={tagStats.map(t => ({ tag: t.name, Publicações: t.count, Audiência: t.totalAudience }))}
                  layout="vertical"
                  margin={{ left: 10, right: 24 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} horizontal={false} />
                  <XAxis type="number" stroke={chartAxisStroke} fontSize={11} tickLine={false} />
                  <YAxis type="category" dataKey="tag" stroke={chartAxisStroke} fontSize={10} tickLine={false} width={110} />
                  <Tooltip
                    contentStyle={chartTooltipSx}
                    formatter={(value, name) => [formatNumberBR(value), name]}
                  />
                  <Bar dataKey="Publicações" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}

          {/* PR Results */}
          {(prResults.themesWorked > 0 || prResults.contentsPublished > 0 || prResults.actionsPerformed > 0) && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>Resultados de PR</Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {[
                  { label: 'Temas Trabalhados', value: prResults.themesWorked, color: '#3b82f6' },
                  { label: 'Conteúdos Divulgados', value: prResults.contentsPublished, color: '#22c55e' },
                  { label: 'Ações Realizadas', value: prResults.actionsPerformed, color: '#f59e0b' }
                ].map(item => (
                  <Grid item xs={12} sm={4} key={item.label}>
                    <Box sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: `${item.color}40`, bgcolor: `${item.color}10`, textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '2.5rem', fontWeight: 900, color: item.color, lineHeight: 1 }}>{item.value}</Typography>
                      <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', fontWeight: 600, mt: 0.5 }}>{item.label}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
              {prResults.byAction && prResults.byAction.length > 0 && (
                <Box sx={sectionPanelSx}>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'text.secondary', mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Ações por Tipo</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {prResults.byAction.map((action, idx) => (
                      <Box key={action.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.5, borderRadius: 1.5, bgcolor: `${pieColors[idx % pieColors.length]}18`, border: '1px solid', borderColor: `${pieColors[idx % pieColors.length]}40` }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: pieColors[idx % pieColors.length] }} />
                        <Typography sx={{ fontSize: '0.75rem', color: 'text.primary', fontWeight: 700 }}>{action.label}</Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>({action.count})</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Cards de artigos para compartilhamento */}
      {filteredArticles && filteredArticles.length > 0 && (
        <ViewSettingsProvider>
          <Box sx={{ mb: 7, pb: 2, width: '100%', px: { xs: 0, sm: 1, md: 2 }, boxSizing: 'border-box' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box sx={{ width: 4, height: 24, borderRadius: operationalRadii.pill, bgcolor: 'var(--wl-primary)' }} />
                <Box>
              <Typography variant="h5" fontWeight={800} color="text.primary">
                Artigos
              </Typography>
              </Box>
            </Box>
            <Chip
              label={`${recentArticles.length} de ${totalAvailableArticles} artigo${totalAvailableArticles === 1 ? '' : 's'}`}
              size="small"
              variant="outlined"
              sx={{
                fontWeight: 650,
                color: 'var(--wl-primary)',
                borderColor: 'var(--wl-border)',
                bgcolor: operationalComponentSurfaces.chip[publicMode].background,
                borderRadius: operationalRadii.pill
              }}
            />
          </Box>

          <Box
            ref={publicArticlesRef}
            sx={{
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              px: { xs: 0, sm: 1, md: 0 },
              pb: 4,
              overflow: 'visible',
              '& .article-card--detailed': {
                height: 'auto !important',
                minHeight: '360px !important',
                border: 'none !important',
                boxShadow: `${operationalElevation.none} !important`
              },
              '& .article-card--detailed > div:nth-of-type(2)': {
                flex: '0 0 auto !important'
              },
              '& .article-card--detailed > div:nth-of-type(2) > div:nth-of-type(3)': {
                marginTop: '12px !important',
                minHeight: '46px !important'
              },
              '& .article-card--detailed > div:last-child': {
                paddingTop: '8px !important',
                paddingBottom: '8px !important',
                marginTop: '0 !important'
              },
              '& .article-card--detailed .article-title--large': {
                fontSize: `${operationalTypography.articleCard.title.fontSize} !important`,
                lineHeight: `${operationalTypography.articleCard.title.lineHeight} !important`,
                minHeight: `${operationalTypography.articleCard.title.minHeight} !important`,
                fontWeight: `${operationalTypography.articleCard.title.fontWeight} !important`,
                marginBottom: '6px !important'
              },
              '& .article-card--detailed:hover': {
                boxShadow: `${operationalElevation.none} !important`
              },
              '& .article-card--detailed [class*="MuiChip-root"]': {
                borderColor: `${brandBorder} !important`
              }
            }}
          >
            <Grid container rowSpacing={2} columnSpacing={1.25} sx={{ overflow: 'visible', width: '100%', m: 0, pb: 2 }}>
              {filteredArticles.map((article) => (
                <Grid
                  item
                  xs={publicGridItem.xs}
                  sm={publicGridItem.sm}
                  md={publicGridItem.md}
                  lg={publicGridItem.lg}
                  xl={publicGridItem.xl}
                  key={article._id}
                >
                  <ArticleCard
                    article={article}
                    variant="detailed"
                    onArticleClick={handleArticleClick}
                    onToggleFavorite={() => {}}
                    onToggleRead={() => {}}
                    onUpdateSentiment={() => {}}
                    onRemoveTag={() => {}}
                    onAddTag={() => {}}
                    onAddToCollection={() => {}}
                    onEdit={() => {}}
                    onDelete={() => {}}
                    onSendNewsletter={() => {}}
                    availableTags={availableTags}
                    userTags={availableTags}
                    selectionMode={false}
                    isSelected={false}
                    hideHoverActions
                    onSelect={() => {}}
                  />
                </Grid>
              ))}
            </Grid>
            {hasMoreArticles && !searchQuery && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 1 }}>
                <Button
                  variant="outlined"
                  onClick={handleLoadMoreArticles}
                  disabled={loadingMoreArticles}
                  startIcon={loadingMoreArticles ? <CircularProgress size={16} color="inherit" /> : null}
                  sx={{
                    borderColor: 'var(--wl-border)',
                    color: 'var(--wl-primary)',
                    fontWeight: 800,
                    textTransform: 'none',
                    borderRadius: 999,
                    px: 3,
                    '&:hover': {
                      borderColor: 'var(--wl-primary)',
                      bgcolor: 'var(--wl-soft)'
                    }
                  }}
                >
                  {loadingMoreArticles ? 'Carregando...' : `Carregar mais ${Math.min(PUBLIC_ARTICLES_PAGE_SIZE, totalAvailableArticles - recentArticles.length)} artigos`}
                </Button>
              </Box>
            )}
          </Box>
          </Box>
        </ViewSettingsProvider>
      )}

      <Box sx={{ textAlign: 'center', pt: 4, pb: 3, mt: 1, borderTop: '1px solid', borderColor: 'var(--wl-border)' }}>
        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.78rem' }}>
          Dados gerenciados por <strong style={{ color: 'var(--wl-primary)' }}>Mensure Monitor</strong>
        </Typography>
      </Box>

      {/* DateRangePicker Dialog */}
      {showDatePicker && (
        <DateRangePicker
          value={customDateRange}
          onChange={handleDateRangeApply}
          onClose={() => setShowDatePicker(false)}
          label="Período Personalizado"
        />
      )}
    </Box>
  );
};

export default PublicDashboardPage;
